// src/SettingsTimezone.js
import React, { useEffect, useState } from 'react';
import API from './api';
import { FileText, Download, Mail, Palette, Building2, Globe2, CalendarClock, GraduationCap, HandCoins, MailCheck, BadgeCheck, Calculator, ShieldCheck, Route, KeyRound } from 'lucide-react';


function SettingsSectionHeading({ icon: Icon, iconClassName, className, children }) {
  return (
    <h3 className={`${className} flex items-center gap-2.5`}>
      <span
        className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${iconClassName}`}
        aria-hidden="true"
      >
        <Icon size={18} strokeWidth={1.75} />
      </span>
      <span className="min-w-0">{children}</span>
    </h3>
  );
}

function ParentEmailPreview({
  brandName,
  timezone,
  includeActions,
  extraMessage,
  enabled,
  reminderType = '24h',
}) {
  const brand = (brandName || '').trim() || 'EarlyCare';
  const tz = timezone || 'America/Los_Angeles';

  const studentLabel = 'Doe, Ava';
  const providerName = 'Jane Smith';
  const whenText = `Jan 15, 2025, 09:00 AM (${tz})`;
  const providerPhone = '(555) 123-4567';
  const parentName = 'Jane Doe';

  const typeLabel = reminderType === '24h' ? '24-hour' : '1-hour';
  const subjectTag = reminderType === '24h' ? '24h' : '1h';

  const subjectText = includeActions
    ? `Reminder[${subjectTag}]: Session with [${providerName}] from [${brand}] – please confirm for ${studentLabel}`
    : `Reminder[${subjectTag}]: Session with [${providerName}] from [${brand}] - ${studentLabel}`;

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-1">
        <h4 className="text-sm font-semibold text-gray-800">
          Parent email preview
        </h4>
        {!enabled && (
          <span className="text-xs text-orange-600">
            Parent notifications are currently disabled – this is only a visual preview.
          </span>
        )}
      </div>

      <div
        className={
          'border rounded-md bg-gray-50 p-4 text-sm text-gray-800' +
          (enabled ? '' : ' opacity-60')
        }
      >
        <div className="border-b pb-2 mb-3 flex flex-col gap-1">
          <div>
            <span className="font-semibold">From:</span>{' '}
            <span>{brand} &lt;no-reply@example.com&gt;</span>
          </div>
          <div>
            <span className="font-semibold">To:</span>{' '}
            <span>Parent &lt;parent@example.com&gt;</span>
          </div>
          <div>
            <span className="font-semibold">Subject:</span>{' '}
            <span>{subjectText}</span>
          </div>
        </div>

        <div className="leading-relaxed">
          <p className="mb-2">Dear {parentName},</p>
          <p className="mb-2">
            This is a {typeLabel} reminder for your scheduled visit:
          </p>
          <ul className="list-disc list-inside mb-3">
            <li>
              <b>Student:</b> {studentLabel}
            </li>
            <li>
              <b>Educator:</b> {providerName} from {brand} (phone: {providerPhone})
            </li>
            <li>
              <b>Date/Time:</b> {whenText}
            </li>
          </ul>

          {includeActions ? (
            <>
              <p className="mb-2">Please confirm:</p>
              <div className="mb-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-semibold text-white bg-green-700 cursor-default"
                >
                  Accept
                </button>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-semibold text-white bg-red-700 cursor-default"
                >
                  Cancel
                </button>
              </div>
              <p className="text-xs text-gray-500 mb-3">
                If the buttons don&apos;t work, parents will also receive plain links
                for Accept / Cancel in the email footer.
              </p>
            </>
          ) : (
            <p className="mb-3">
              This email is for your information only. No confirmation is required at
              this time.
            </p>
          )}

          {extraMessage && (
            <div className="mb-3">
              <p className="text-xs text-gray-500 mb-1">
                Additional note:
              </p>
              <div
                className="text-sm text-gray-800 border-l-4 border-blue-300 pl-3"
                style={{ whiteSpace: 'pre-line' }}
              >
                {extraMessage}
              </div>
            </div>
          )}

          <p className="mt-4">
            Thank you,
            <br />
            Scheduling Department / {brand}
          </p>
        </div>
      </div>
    </div>
  );
}

/** =========================================================
 * Compact range inputs (def/min/max) – small UI
 * ========================================================= */
function MiniNumberInput({ value, onChange, placeholder, ariaLabel }) {
  return (
    <input
      type="number"
      inputMode="numeric"
      min="0"
      className="w-[72px] md:w-[84px] px-2 py-1 text-xs rounded-md border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-amber-200"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={ariaLabel}
    />
  );
}

function Range3({ label, a, b, c, onA, onB, onC }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-[64px] text-xs font-semibold text-amber-900">
        {label}
      </div>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-slate-500">def</span>
          <MiniNumberInput value={a} onChange={onA} placeholder="45" ariaLabel={`${label} default`} />
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-slate-500">min</span>
          <MiniNumberInput value={b} onChange={onB} placeholder="45" ariaLabel={`${label} min`} />
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-slate-500">max</span>
          <MiniNumberInput value={c} onChange={onC} placeholder="90" ariaLabel={`${label} max`} />
        </div>
      </div>
    </div>
  );
}

/**
 * DEFAULT SERVICE RULES – moraju biti u sync-u sa backend DEFAULT_SERVICE_DURATION_RULES
 */
const DEFAULT_SERVICE_DURATION_RULES = {
  four_hours_block_min: 240,
  paid_break_min: 10,
  no_show_max_min: 15,
  payroll_chain_gap_enabled: true,
  payroll_chain_gap_max_min: 90,
  service_min_shortfall_percent: 50,
  service_max_excess_percent: 0,
  gps_route_max_excess_percent: 50,

  // legacy fixed (kept for compatibility)
  ei_fixed_min: 45,
  st_fixed_min: 45,
  pi_fixed_min: 90,

  // NEW range UI fields for EI/PI/ST (safe defaults -> fixed behavior)
  ei_default_min: 45,
  ei_min: 45,
  ei_max: 45,

  pi_default_min: 90,
  pi_min: 90,
  pi_max: 90,

  st_default_min: 45,
  st_min: 45,
  st_max: 45,

  aba_default_min: 90,
  aba_min: 45,
  aba_max: 240,

  otpt_default_min: 45,
  otpt_min: 45,
  otpt_max: 90,

  event_default_min: 90,
  event_min: 45,
  event_max: 240,
};

// BE generic default-i za OBJ_* koje UI ne menja, ali moramo da ih zadržimo
const GENERIC_BACKEND_DEFAULTS = {
  OBJ_MIN_TRIGGER_MIN: 30,
  OBJ_BILLED_MIN: 45,
  OBJ_BILLED_MAX: 60,
};

// NEW: default mentor fee per visit (USD)
const DEFAULT_MENTOR_FEES = {
  ei: 0,
  pi: 0,
  aba: 0,
  ot: 0,
  pt: 0,
  st: 0,
};

// NEW: default training rules (system_settings.key_name = 'training_rules')
const DEFAULT_TRAINING_RULES = {
  shadow: { min_visits: 20, min_days: 14 },
  supervised: { min_visits: 40, min_days: 30 },
  min_monthly_visits: 12,
  stuck_after_days: 60,
};

// NEW: default clinic settings (meta only; addresses are in organization_locations)
const DEFAULT_CLINIC_SETTINGS = {
  enabled: false,
  name: '',
  default_location_id: null,
};

const DEFAULT_SMART_TRIP_AUTOMATION_SETTINGS = {
  smart_assist_enabled: true,
  autopilot_available: false,
  allow_user_opt_in: false,
  label: 'Smart Trip Automation',
  version: 1,
};

const DEFAULT_PAYROLL_EVV_SETTINGS = {
  require_evv_complete_for_payroll: false,
  apply_only_when_evv_required: true,
};

function SettingsTimezone() {
  const [timezones, setTimezones] = useState([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingTz, setSavingTz] = useState(false);

  const [start, setStart] = useState('08:00');
  const [end, setEnd] = useState('18:00');
  const [savingWd, setSavingWd] = useState(false);

  const [parentNotifEnabled, setParentNotifEnabled] = useState(false);
  const [parentNotifIncludeActions, setParentNotifIncludeActions] = useState(true);
  const [parentNotifExtraMessage, setParentNotifExtraMessage] = useState('');
  const [savingParentNotif, setSavingParentNotif] = useState(false);

  const [brandName, setBrandName] = useState('');
  const [savingBrand, setSavingBrand] = useState(false);

  // logo
  const [brandLogoUrl, setBrandLogoUrl] = useState('');
  const [brandLogoVer, setBrandLogoVer] = useState(1); // cache-busting
  const [logoUploadFile, setLogoUploadFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [successMsg, setSuccessMsg] = useState('');

  // Preview reminder type: '24h' or '1h'
  const [previewType, setPreviewType] = useState('24h');

  // Mailing list modal state
  const [mailingModalOpen, setMailingModalOpen] = useState(false);
  const [mailingParents, setMailingParents] = useState([]);
  const [mailingLoading, setMailingLoading] = useState(false);
  const [mailingSaving, setMailingSaving] = useState(false);

  // Security / password
  const [hasPassword, setHasPassword] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Org security settings (2FA / trusted devices)
  const [securitySettings, setSecuritySettings] = useState({
    require_2fa_admin: true,
    require_2fa_teacher: false,
    allow_trusted_devices: true,
    trusted_device_days: 30,
    require_2fa_super_admin: true,
  });
  const [savingSecuritySettings, setSavingSecuritySettings] = useState(false);
  const [securitySettingsLoading, setSecuritySettingsLoading] = useState(false);

  // Org Smart Trip Automation visibility settings
  const [smartTripAutomationSettings, setSmartTripAutomationSettings] = useState(
    DEFAULT_SMART_TRIP_AUTOMATION_SETTINGS
  );
  const [smartTripAutomationLoading, setSmartTripAutomationLoading] = useState(false);
  const [savingSmartTripAutomation, setSavingSmartTripAutomation] = useState(false);

  // Org invoices (for org admin)
  const [orgInvoices, setOrgInvoices] = useState([]);
  const [orgInvoicesLoading, setOrgInvoicesLoading] = useState(false);
  const [orgInvoicesError, setOrgInvoicesError] = useState('');

  // Service duration rules (UI flat)
  const [serviceRules, setServiceRules] = useState(DEFAULT_SERVICE_DURATION_RULES);
  const [serviceToleranceErrors, setServiceToleranceErrors] = useState({});
  const [rawDurationRules, setRawDurationRules] = useState(null);
  const [savingServiceRules, setSavingServiceRules] = useState(false);

  // mentorship fee settings
  const [mentorFees, setMentorFees] = useState(DEFAULT_MENTOR_FEES);
  const [savingMentorFees, setSavingMentorFees] = useState(false);

  // payroll EVV settings
  const [payrollEvvSettings, setPayrollEvvSettings] = useState(DEFAULT_PAYROLL_EVV_SETTINGS);
  const [savingPayrollEvvSettings, setSavingPayrollEvvSettings] = useState(false);

  const [evvTolerances, setEvvTolerances] = useState({ time_tolerance_minutes: '', location_radius_meters: '' });
  const [loadingEvvTolerances, setLoadingEvvTolerances] = useState(true);
  const [savingEvvTolerances, setSavingEvvTolerances] = useState(false);
  const [evvTolerancesError, setEvvTolerancesError] = useState('');
  const [evvTolerancesLoaded, setEvvTolerancesLoaded] = useState(false);
  const [evvTolerancesLoadAttempt, setEvvTolerancesLoadAttempt] = useState(0);

  useEffect(() => {
    let mounted = true;
    setLoadingEvvTolerances(true);
    setEvvTolerancesLoaded(false);
    setEvvTolerancesError('');
    API.get('/admin/evv-tolerance-settings').then(({ data }) => {
      if (!data || !Number.isFinite(Number(data.time_tolerance_minutes)) || data.time_tolerance_minutes == null ||
          !Number.isFinite(Number(data.location_radius_meters)) || Number(data.location_radius_meters) <= 0) {
        throw new Error('Invalid EVV settings response');
      }
      if (!mounted) return;
      setEvvTolerances({ time_tolerance_minutes: data.time_tolerance_minutes, location_radius_meters: data.location_radius_meters });
      setEvvTolerancesLoaded(true);
    }).catch(error => {
      if (mounted) setEvvTolerancesError(error?.response?.data?.error || 'Could not load EVV tolerances. Retry before saving.');
    }).finally(() => { if (mounted) setLoadingEvvTolerances(false); });
    return () => { mounted = false; };
  }, [evvTolerancesLoadAttempt]);

  const evvToleranceInputValid = (value, min, max) => String(value).trim() !== '' && Number.isInteger(Number(value)) && Number(value) >= min && Number(value) <= max;
  const evvTolerancesValid = evvToleranceInputValid(evvTolerances.time_tolerance_minutes, 0, 120) && evvToleranceInputValid(evvTolerances.location_radius_meters, 1, 100000);
  const saveEvvTolerances = async () => {
    if (!evvTolerancesLoaded || !evvTolerancesValid || savingEvvTolerances) return;
    setSavingEvvTolerances(true);
    setEvvTolerancesError('');
    try {
      const { data } = await API.post('/admin/evv-tolerance-settings', {
        time_tolerance_minutes: Number(evvTolerances.time_tolerance_minutes),
        location_radius_meters: Number(evvTolerances.location_radius_meters),
      });
      setEvvTolerances({ time_tolerance_minutes: data.time_tolerance_minutes, location_radius_meters: data.location_radius_meters });
      flash('EVV tolerances updated!');
    } catch (error) {
      setEvvTolerancesError(error?.response?.data?.error || 'Could not save EVV tolerances. Please retry.');
    } finally { setSavingEvvTolerances(false); }
  };

  // training rules settings
  const [trainingRules, setTrainingRules] = useState(DEFAULT_TRAINING_RULES);
  const [savingTrainingRules, setSavingTrainingRules] = useState(false);

  // clinic meta + org locations
  const [clinicEnabled, setClinicEnabled] = useState(DEFAULT_CLINIC_SETTINGS.enabled);
  const [clinicDisplayName, setClinicDisplayName] = useState(DEFAULT_CLINIC_SETTINGS.name);
  const [clinicDefaultLocationId, setClinicDefaultLocationId] = useState(DEFAULT_CLINIC_SETTINGS.default_location_id);

  const [orgLocations, setOrgLocations] = useState([]);
  const [orgLocationsLoading, setOrgLocationsLoading] = useState(false);
  const [savingClinicMeta, setSavingClinicMeta] = useState(false);

  // Add/Edit location modal
  const [locModalOpen, setLocModalOpen] = useState(false);
  const [locEditingId, setLocEditingId] = useState(null);
  const [locName, setLocName] = useState('');
  const [locStreet, setLocStreet] = useState('');
  const [locCity, setLocCity] = useState('');
  const [locState, setLocState] = useState('');
  const [locZip, setLocZip] = useState('');
  const [locCountry, setLocCountry] = useState('');
  const [locDoGeocode, setLocDoGeocode] = useState(true);
  const [locSubmitting, setLocSubmitting] = useState(false);

  const formatLocationAddr = (l) => {
    if (!l) return '';
    const parts = [];
    if (l.street) parts.push(l.street);
    const cityLine = [l.city, l.state, l.zip].filter(Boolean).join(' ');
    if (cityLine) parts.push(cityLine);
    if (l.country) parts.push(l.country);
    return parts.join(', ');
  };

  // central toast helper
  const flash = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 2000);
  };

  const loadOrgLocations = async () => {
    setOrgLocationsLoading(true);
    try {
      const res = await API.get('/org-locations');
      const locations = res.data?.locations || [];
      const cs = res.data?.clinic_settings || {};

      setOrgLocations(locations);
      setClinicEnabled(cs?.enabled ?? DEFAULT_CLINIC_SETTINGS.enabled);
      setClinicDisplayName(cs?.name ?? DEFAULT_CLINIC_SETTINGS.name);
      setClinicDefaultLocationId(
        cs?.default_location_id ?? DEFAULT_CLINIC_SETTINGS.default_location_id
      );
    } catch (e) {
      console.warn('Failed to load org locations', e?.response?.data || e.message);
      setOrgLocations([]);
      setClinicEnabled(DEFAULT_CLINIC_SETTINGS.enabled);
      setClinicDisplayName(DEFAULT_CLINIC_SETTINGS.name);
      setClinicDefaultLocationId(DEFAULT_CLINIC_SETTINGS.default_location_id);
    } finally {
      setOrgLocationsLoading(false);
    }
  };

  const primaryLocation =
    orgLocations.find((l) => Number(l.id) === Number(clinicDefaultLocationId)) || null;

  const openAddLocation = () => {
    setLocEditingId(null);
    setLocName('');
    setLocStreet('');
    setLocCity('');
    setLocState('');
    setLocZip('');
    setLocCountry('');
    setLocDoGeocode(true);
    setLocModalOpen(true);
  };

  const openEditLocation = (loc) => {
    setLocEditingId(loc?.id || null);
    setLocName(loc?.name || '');
    setLocStreet(loc?.street || '');
    setLocCity(loc?.city || '');
    setLocState(loc?.state || '');
    setLocZip(loc?.zip || '');
    setLocCountry(loc?.country || '');
    setLocDoGeocode(false); // default: don't re-geocode unless user wants
    setLocModalOpen(true);
  };

  const submitLocation = async () => {
    const nm = (locName || '').trim();
    if (!nm) {
      alert('Location name is required.');
      return;
    }

    setLocSubmitting(true);
    try {
      const payload = {
        name: nm,
        street: (locStreet || '').trim(),
        city: (locCity || '').trim(),
        state: (locState || '').trim(),
        zip: (locZip || '').trim(),
        country: (locCountry || '').trim(),
        do_geocode: true,
      };

      if (locEditingId) {
        await API.put(`/org-locations/${locEditingId}`, payload);
        flash('Location updated!');
      } else {
        await API.post('/org-locations', payload);
        flash('Location created!');
      }

      setLocModalOpen(false);
      await loadOrgLocations();
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to save location');
    } finally {
      setLocSubmitting(false);
    }
  };

  const deactivateLocation = async (id) => {
    if (!id) return;
    if (!window.confirm('Deactivate this location?')) return;

    try {
      await API.delete(`/org-locations/${id}`);
      flash('Location deactivated!');
      await loadOrgLocations();
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to deactivate location');
    }
  };

  const setPrimaryLocation = async (id) => {
    if (!id) return;
    try {
      await API.put(`/org-locations/${id}/primary`);
      setClinicDefaultLocationId(id);
      flash('Primary clinic location updated!');
      await loadOrgLocations();
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to set primary location');
    }
  };

  const saveClinicMeta = async () => {
    // client-side sanity:
    if (clinicEnabled && orgLocations.length > 0 && !clinicDefaultLocationId) {
      alert('Please choose a Primary clinic location (or disable clinic).');
      return;
    }

    setSavingClinicMeta(true);
    try {
      const payload = {
        enabled: !!clinicEnabled,
        name: (clinicDisplayName || '').trim(),
        default_location_id: clinicEnabled ? (clinicDefaultLocationId ?? null) : null,
      };

      const r = await API.post('/org-locations/settings', payload);
      const d = r.data || payload;

      setClinicEnabled(!!d.enabled);
      setClinicDisplayName(d.name || '');
      setClinicDefaultLocationId(d.default_location_id ?? null);

      flash('Clinic settings updated!');
      await loadOrgLocations();
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to save clinic settings');
    } finally {
      setSavingClinicMeta(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // timezone
        const tz = await API.get('/timezone-settings');
        setTimezones(tz.data.timezones || []);
        setSelected(tz.data.selected || '');

        // workday
        const wd = await API.get('/workday-settings');
        setStart((wd.data?.start || '08:00').slice(0, 5));
        setEnd((wd.data?.end || '18:00').slice(0, 5));

        // parent notifications
        const pn = await API.get('/parent-notifications-settings');
        setParentNotifEnabled(!!pn.data.enabled);
        setParentNotifIncludeActions(
          pn.data.include_actions !== undefined ? !!pn.data.include_actions : true
        );
        setParentNotifExtraMessage(pn.data.extra_message || '');

        // service duration rules – BE -> UI shape
        try {
          const srRes = await API.get('/service-duration-settings?strict=1');
          const rules = srRes.data || {};
          setRawDurationRules(rules);

          const g = rules.generic || {};
          const EI = rules.EI || {};
          const PI = rules.PI || {};
          const ABA = rules.ABA || {};
          const OT = rules.OT || {};
          const ST = rules.ST || {};
          const EVENT = rules.EVENT || {};

          const eiFixed = Number(EI.fixed ?? DEFAULT_SERVICE_DURATION_RULES.ei_fixed_min);
          const piFixed = Number(PI.fixed ?? DEFAULT_SERVICE_DURATION_RULES.pi_fixed_min);
          const stFixed = Number(ST.fixed ?? DEFAULT_SERVICE_DURATION_RULES.st_fixed_min);

          const eiDef = Number(EI.default ?? eiFixed ?? DEFAULT_SERVICE_DURATION_RULES.ei_default_min);
          const eiMin = Number(EI.min ?? eiDef ?? DEFAULT_SERVICE_DURATION_RULES.ei_min);
          const eiMax = Number(EI.max ?? eiDef ?? DEFAULT_SERVICE_DURATION_RULES.ei_max);

          const piDef = Number(PI.default ?? piFixed ?? DEFAULT_SERVICE_DURATION_RULES.pi_default_min);
          const piMin = Number(PI.min ?? piDef ?? DEFAULT_SERVICE_DURATION_RULES.pi_min);
          const piMax = Number(PI.max ?? piDef ?? DEFAULT_SERVICE_DURATION_RULES.pi_max);

          const stDef = Number(ST.default ?? stFixed ?? DEFAULT_SERVICE_DURATION_RULES.st_default_min);
          const stMin = Number(ST.min ?? stDef ?? DEFAULT_SERVICE_DURATION_RULES.st_min);
          const stMax = Number(ST.max ?? stDef ?? DEFAULT_SERVICE_DURATION_RULES.st_max);

          setServiceRules({
            four_hours_block_min: Number(g.FOUR_HOURS_MIN ?? DEFAULT_SERVICE_DURATION_RULES.four_hours_block_min),
            paid_break_min: Number(g.PAID_BREAK_MIN ?? DEFAULT_SERVICE_DURATION_RULES.paid_break_min),
            no_show_max_min: Number(g.NOSHOW_MAX_MIN ?? DEFAULT_SERVICE_DURATION_RULES.no_show_max_min),
            payroll_chain_gap_enabled: g.PAYROLL_CHAIN_GAP_ENABLED !== false,
            payroll_chain_gap_max_min: Number(g.PAYROLL_CHAIN_GAP_MAX_MIN ?? DEFAULT_SERVICE_DURATION_RULES.payroll_chain_gap_max_min),
            service_min_shortfall_percent: Number(g.SERVICE_MIN_SHORTFALL_PERCENT ?? DEFAULT_SERVICE_DURATION_RULES.service_min_shortfall_percent),
            service_max_excess_percent: Number(g.SERVICE_MAX_EXCESS_PERCENT ?? DEFAULT_SERVICE_DURATION_RULES.service_max_excess_percent),
            gps_route_max_excess_percent: Number(g.GPS_ROUTE_MAX_EXCESS_PERCENT ?? DEFAULT_SERVICE_DURATION_RULES.gps_route_max_excess_percent),

            ei_fixed_min: Number(eiFixed || DEFAULT_SERVICE_DURATION_RULES.ei_fixed_min),
            pi_fixed_min: Number(piFixed || DEFAULT_SERVICE_DURATION_RULES.pi_fixed_min),
            st_fixed_min: Number(stFixed || DEFAULT_SERVICE_DURATION_RULES.st_fixed_min),

            ei_default_min: Number(eiDef || DEFAULT_SERVICE_DURATION_RULES.ei_default_min),
            ei_min: Number(eiMin || DEFAULT_SERVICE_DURATION_RULES.ei_min),
            ei_max: Number(eiMax || DEFAULT_SERVICE_DURATION_RULES.ei_max),

            pi_default_min: Number(piDef || DEFAULT_SERVICE_DURATION_RULES.pi_default_min),
            pi_min: Number(piMin || DEFAULT_SERVICE_DURATION_RULES.pi_min),
            pi_max: Number(piMax || DEFAULT_SERVICE_DURATION_RULES.pi_max),

            st_default_min: Number(stDef || DEFAULT_SERVICE_DURATION_RULES.st_default_min),
            st_min: Number(stMin || DEFAULT_SERVICE_DURATION_RULES.st_min),
            st_max: Number(stMax || DEFAULT_SERVICE_DURATION_RULES.st_max),

            aba_default_min: Number(ABA.default ?? DEFAULT_SERVICE_DURATION_RULES.aba_default_min),
            aba_min: Number(ABA.min ?? DEFAULT_SERVICE_DURATION_RULES.aba_min),
            aba_max: Number(ABA.max ?? DEFAULT_SERVICE_DURATION_RULES.aba_max),

            otpt_default_min: Number(OT.default ?? DEFAULT_SERVICE_DURATION_RULES.otpt_default_min),
            otpt_min: Number(OT.min ?? DEFAULT_SERVICE_DURATION_RULES.otpt_min),
            otpt_max: Number(OT.max ?? DEFAULT_SERVICE_DURATION_RULES.otpt_max),

            event_default_min: Number(EVENT.default ?? DEFAULT_SERVICE_DURATION_RULES.event_default_min),
            event_min: Number(EVENT.min ?? DEFAULT_SERVICE_DURATION_RULES.event_min),
            event_max: Number(EVENT.max ?? DEFAULT_SERVICE_DURATION_RULES.event_max),
          });
        } catch (e) {
          console.warn('No service duration settings yet', e?.response?.data || e.message);
          setRawDurationRules(null);
          setServiceRules(DEFAULT_SERVICE_DURATION_RULES);
        }

        // mentor fees (optional endpoint)
        try {
          const mfRes = await API.get('/mentor-fee-settings');
          const d = mfRes.data || {};
          setMentorFees({
            ei: Number(d.EI ?? d.ei ?? DEFAULT_MENTOR_FEES.ei) || 0,
            pi: Number(d.PI ?? d.pi ?? DEFAULT_MENTOR_FEES.pi) || 0,
            aba: Number(d.ABA ?? d.aba ?? DEFAULT_MENTOR_FEES.aba) || 0,
            ot: Number(d.OT ?? d.ot ?? DEFAULT_MENTOR_FEES.ot) || 0,
            pt: Number(d.PT ?? d.pt ?? DEFAULT_MENTOR_FEES.pt) || 0,
            st: Number(d.ST ?? d.st ?? DEFAULT_MENTOR_FEES.st) || 0,
          });
        } catch (e) {
          console.warn('No mentor fee settings yet', e?.response?.data || e.message);
          setMentorFees(DEFAULT_MENTOR_FEES);
        }

        // payroll EVV validation
        try {
          const evvRes = await API.get('/admin/payroll-evv-settings');
          const d = evvRes.data || {};
          setPayrollEvvSettings({
            require_evv_complete_for_payroll: !!d.require_evv_complete_for_payroll,
            apply_only_when_evv_required: true,
          });
        } catch (e) {
          console.warn('No payroll EVV settings yet', e?.response?.data || e.message);
          setPayrollEvvSettings(DEFAULT_PAYROLL_EVV_SETTINGS);
        }

        // training rules
        try {
          const trRes = await API.get('/training-rules-settings');
          const d = trRes.data || {};

          let parsed = d;
          if (d && typeof d.value_text === 'string') {
            try {
              parsed = JSON.parse(d.value_text);
            } catch {
              parsed = DEFAULT_TRAINING_RULES;
            }
          }

          setTrainingRules({
            shadow: {
              min_visits: Number(parsed?.shadow?.min_visits ?? DEFAULT_TRAINING_RULES.shadow.min_visits) || DEFAULT_TRAINING_RULES.shadow.min_visits,
              min_days: Number(parsed?.shadow?.min_days ?? DEFAULT_TRAINING_RULES.shadow.min_days) || DEFAULT_TRAINING_RULES.shadow.min_days,
            },
            supervised: {
              min_visits: Number(parsed?.supervised?.min_visits ?? DEFAULT_TRAINING_RULES.supervised.min_visits) || DEFAULT_TRAINING_RULES.supervised.min_visits,
              min_days: Number(parsed?.supervised?.min_days ?? DEFAULT_TRAINING_RULES.supervised.min_days) || DEFAULT_TRAINING_RULES.supervised.min_days,
            },
            min_monthly_visits: Number(parsed?.min_monthly_visits ?? DEFAULT_TRAINING_RULES.min_monthly_visits) || DEFAULT_TRAINING_RULES.min_monthly_visits,
            stuck_after_days: Number(parsed?.stuck_after_days ?? DEFAULT_TRAINING_RULES.stuck_after_days) || DEFAULT_TRAINING_RULES.stuck_after_days,
          });
        } catch (e) {
          console.warn('No training rules yet', e?.response?.data || e.message);
          setTrainingRules(DEFAULT_TRAINING_RULES);
        }

        // brand name
        try {
          const b = await API.get('/brand-settings');
          setBrandName(b.data?.brand_name || '');
        } catch (e) {
          console.warn('No brand settings yet', e?.response?.data || e.message);
        }

        // brand logo
        try {
          const logoRes = await API.get('/brand-logo');
          const url = logoRes.data?.logo_url || null;
          setBrandLogoUrl(url || '');
        } catch (e) {
          console.warn('No brand logo yet', e?.response?.data || e.message);
          setBrandLogoUrl('');
        }

        // org locations + clinic meta
        await loadOrgLocations();

        // security info
        try {
          const sec = await API.get('/me/security');
          setHasPassword(!!sec.data?.has_password);
        } catch (e) {
          console.warn('No security info yet', e?.response?.data || e.message);
          setHasPassword(null);
        }

        // org security settings
        try {
          setSecuritySettingsLoading(true);
          const secRes = await API.get('/admin/security-settings');
          const s = secRes.data || {};

          setSecuritySettings({
            require_2fa_admin:
              s.require_2fa_admin !== undefined ? !!s.require_2fa_admin : true,
            require_2fa_teacher:
              s.require_2fa_teacher !== undefined ? !!s.require_2fa_teacher : false,
            allow_trusted_devices:
              s.allow_trusted_devices !== undefined ? !!s.allow_trusted_devices : true,
            trusted_device_days:
              Number(s.trusted_device_days || 30) || 30,
            require_2fa_super_admin:
              s.require_2fa_super_admin !== undefined ? !!s.require_2fa_super_admin : true,
          });
        } catch (e) {
          console.warn('Failed to load security settings', e?.response?.data || e.message);
        } finally {
          setSecuritySettingsLoading(false);
        }

        // org Smart Trip Automation visibility settings
        try {
          setSmartTripAutomationLoading(true);
          const smartTripRes = await API.get('/admin/smart-trip-automation-settings');
          const s = smartTripRes.data || {};
          const autopilotAvailable = !!s.autopilot_available;

          setSmartTripAutomationSettings({
            ...DEFAULT_SMART_TRIP_AUTOMATION_SETTINGS,
            smart_assist_enabled: s.smart_assist_enabled !== false,
            autopilot_available: autopilotAvailable,
            allow_user_opt_in: autopilotAvailable ? !!s.allow_user_opt_in : false,
          });
        } catch (e) {
          console.warn('Failed to load Smart Trip Automation settings', e?.response?.data || e.message);
          setSmartTripAutomationSettings(DEFAULT_SMART_TRIP_AUTOMATION_SETTINGS);
        } finally {
          setSmartTripAutomationLoading(false);
        }

        // org invoices
        try {
          setOrgInvoicesLoading(true);
          setOrgInvoicesError('');
          const invRes = await API.get('/org/invoices');
          setOrgInvoices(invRes.data || []);
        } catch (e) {
          console.warn('Failed to load org invoices', e?.response?.data || e.message);
          setOrgInvoicesError('Failed to load invoices.');
        } finally {
          setOrgInvoicesLoading(false);
        }
      } catch (e) {
        console.error(e);
        alert('Failed to load settings');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveTimezone = async () => {
    if (!selected) return;
    setSavingTz(true);
    try {
      await API.post('/timezone-settings', { timezone: selected });
      flash('Timezone updated!');
    } catch {
      alert('Failed to save timezone');
    } finally {
      setSavingTz(false);
    }
  };

  const saveWorkday = async () => {
    setSavingWd(true);
    try {
      await API.post('/workday-settings', { start, end });
      flash('Workday updated!');
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to save workday');
    } finally {
      setSavingWd(false);
    }
  };

  const saveParentNotif = async () => {
    setSavingParentNotif(true);
    try {
      await API.post('/parent-notifications-settings', {
        enabled: parentNotifEnabled,
        include_actions: parentNotifIncludeActions,
        extra_message: parentNotifExtraMessage,
      });
      flash('Parent notifications updated!');
    } catch {
      alert('Failed to save parent notifications setting');
    } finally {
      setSavingParentNotif(false);
    }
  };

  const saveBrand = async () => {
    const name = (brandName || '').trim();
    if (!name) {
      alert('Brand name is required');
      return;
    }
    setSavingBrand(true);
    try {
      await API.post('/brand-settings', { brand_name: name });
      flash('Brand name updated!');
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to save brand name');
    } finally {
      setSavingBrand(false);
    }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setLogoUploadFile(file || null);
  };

  const saveBrandLogo = async () => {
    if (!logoUploadFile) {
      alert('Please choose a PNG or JPG logo file first.');
      return;
    }
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', logoUploadFile);

      const resp = await API.post('/brand-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const newUrl = resp.data?.logo_url || '';
      if (newUrl) {
        setBrandLogoUrl(newUrl);
        setBrandLogoVer((v) => v + 1);
      } else {
        setBrandLogoUrl('');
      }

      setLogoUploadFile(null);
      flash('Brand logo updated!');
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleServiceRuleChange = (field, value) => {
    setServiceToleranceErrors((prev) => ({ ...prev, [field]: undefined }));
    setServiceRules((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveServiceRules = async () => {
    if (!rawDurationRules) { alert('Reload service settings before saving.'); return; }
    const limits = { service_min_shortfall_percent: 100, service_max_excess_percent: 100, gps_route_max_excess_percent: 500 };
    const errors = {};
    for (const [field, max] of Object.entries(limits)) {
      const value = serviceRules[field];
      if (value == null || String(value).trim() === '' || !Number.isFinite(Number(value)) || Number(value) < 0 || Number(value) > max) errors[field] = `Enter a number from 0 to ${max}.`;
    }
    setServiceToleranceErrors(errors);
    if (Object.keys(errors).length) return;
    setSavingServiceRules(true);
    try {
      const normalize = (key) => {
        const raw = Number(serviceRules[key]);
        if (Number.isFinite(raw) && raw > 0) return Math.round(raw);
        return DEFAULT_SERVICE_DURATION_RULES[key];
      };

      const normalizeRangeValue = (key, min, max) => {
        const raw = Number(serviceRules[key]);
        const fallback = DEFAULT_SERVICE_DURATION_RULES[key];
        const value = Number.isFinite(raw) ? Math.round(raw) : fallback;
        return Math.min(Math.max(value, min), max);
      };

      const clampRange = (defV, minV, maxV) => {
        let mn = Number(minV);
        let mx = Number(maxV);
        let df = Number(defV);
        if (!Number.isFinite(mn) || mn <= 0) mn = df;
        if (!Number.isFinite(mx) || mx <= 0) mx = df;
        if (!Number.isFinite(df) || df <= 0) df = mn;

        if (mn > mx) {
          const t = mn;
          mn = mx;
          mx = t;
        }
        df = Math.min(Math.max(df, mn), mx);
        return { df, mn, mx };
      };

      const current = rawDurationRules || {};
      const currentGeneric = current.generic || {};

      const eiR = clampRange(normalize('ei_default_min'), normalize('ei_min'), normalize('ei_max'));
      const piR = clampRange(normalize('pi_default_min'), normalize('pi_min'), normalize('pi_max'));
      const stR = clampRange(normalize('st_default_min'), normalize('st_min'), normalize('st_max'));

      const abaR = clampRange(normalize('aba_default_min'), normalize('aba_min'), normalize('aba_max'));
      const otptR = clampRange(normalize('otpt_default_min'), normalize('otpt_min'), normalize('otpt_max'));
      const eventR = clampRange(normalize('event_default_min'), normalize('event_min'), normalize('event_max'));

      const useEiRange = !(eiR.df === eiR.mn && eiR.df === eiR.mx);
      const usePiRange = !(piR.df === piR.mn && piR.df === piR.mx);
      const useStRange = !(stR.df === stR.mn && stR.df === stR.mx);

      const payload = {
        EI: useEiRange ? { type: 'range', default: eiR.df, min: eiR.mn, max: eiR.mx } : { type: 'fixed', fixed: eiR.df },
        PI: usePiRange ? { type: 'range', default: piR.df, min: piR.mn, max: piR.mx } : { type: 'fixed', fixed: piR.df },
        ST: useStRange ? { type: 'range', default: stR.df, min: stR.mn, max: stR.mx } : { type: 'fixed', fixed: stR.df },

        ABA: { type: 'range', default: abaR.df, min: abaR.mn, max: abaR.mx },
        OT: { type: 'range', default: otptR.df, min: otptR.mn, max: otptR.mx },
        PT: current.PT && otptR.df === Number(current.OT?.default) && otptR.mn === Number(current.OT?.min) && otptR.mx === Number(current.OT?.max)
          ? current.PT : { type: 'range', default: otptR.df, min: otptR.mn, max: otptR.mx },
        EVENT: { type: 'range', default: eventR.df, min: eventR.mn, max: eventR.mx },

        generic: {
          ...currentGeneric,
          SERVICE_MIN_SHORTFALL_PERCENT: Number(serviceRules.service_min_shortfall_percent),
          SERVICE_MAX_EXCESS_PERCENT: Number(serviceRules.service_max_excess_percent),
          GPS_ROUTE_MAX_EXCESS_PERCENT: Number(serviceRules.gps_route_max_excess_percent),
          OBJ_MIN_TRIGGER_MIN: Number(currentGeneric.OBJ_MIN_TRIGGER_MIN ?? GENERIC_BACKEND_DEFAULTS.OBJ_MIN_TRIGGER_MIN),
          OBJ_BILLED_MIN: Number(currentGeneric.OBJ_BILLED_MIN ?? GENERIC_BACKEND_DEFAULTS.OBJ_BILLED_MIN),
          OBJ_BILLED_MAX: Number(currentGeneric.OBJ_BILLED_MAX ?? GENERIC_BACKEND_DEFAULTS.OBJ_BILLED_MAX),
          NOSHOW_MAX_MIN: normalize('no_show_max_min'),
          FOUR_HOURS_MIN: normalize('four_hours_block_min'),
          PAID_BREAK_MIN: normalize('paid_break_min'),
          PAYROLL_CHAIN_GAP_ENABLED: serviceRules.payroll_chain_gap_enabled !== false,
          PAYROLL_CHAIN_GAP_MAX_MIN: normalizeRangeValue('payroll_chain_gap_max_min', 30, 480),
        },
      };

      const res = await API.post('/service-duration-settings', payload);
      const merged = res.data || payload;
      setRawDurationRules(merged);

      // refresh UI state from merged
      const g = merged.generic || {};
      const EI = merged.EI || {};
      const PI = merged.PI || {};
      const ABA = merged.ABA || {};
      const OT = merged.OT || {};
      const ST = merged.ST || {};
      const EVENT = merged.EVENT || {};

      const eiFixed = Number(EI.fixed ?? DEFAULT_SERVICE_DURATION_RULES.ei_fixed_min);
      const piFixed = Number(PI.fixed ?? DEFAULT_SERVICE_DURATION_RULES.pi_fixed_min);
      const stFixed = Number(ST.fixed ?? DEFAULT_SERVICE_DURATION_RULES.st_fixed_min);

      const eiDef = Number(EI.default ?? eiFixed ?? DEFAULT_SERVICE_DURATION_RULES.ei_default_min);
      const eiMin = Number(EI.min ?? eiDef ?? DEFAULT_SERVICE_DURATION_RULES.ei_min);
      const eiMax = Number(EI.max ?? eiDef ?? DEFAULT_SERVICE_DURATION_RULES.ei_max);

      const piDef = Number(PI.default ?? piFixed ?? DEFAULT_SERVICE_DURATION_RULES.pi_default_min);
      const piMin = Number(PI.min ?? piDef ?? DEFAULT_SERVICE_DURATION_RULES.pi_min);
      const piMax = Number(PI.max ?? piDef ?? DEFAULT_SERVICE_DURATION_RULES.pi_max);

      const stDef = Number(ST.default ?? stFixed ?? DEFAULT_SERVICE_DURATION_RULES.st_default_min);
      const stMin = Number(ST.min ?? stDef ?? DEFAULT_SERVICE_DURATION_RULES.st_min);
      const stMax = Number(ST.max ?? stDef ?? DEFAULT_SERVICE_DURATION_RULES.st_max);

      setServiceRules({
        four_hours_block_min: Number(g.FOUR_HOURS_MIN ?? DEFAULT_SERVICE_DURATION_RULES.four_hours_block_min),
        paid_break_min: Number(g.PAID_BREAK_MIN ?? DEFAULT_SERVICE_DURATION_RULES.paid_break_min),
        no_show_max_min: Number(g.NOSHOW_MAX_MIN ?? DEFAULT_SERVICE_DURATION_RULES.no_show_max_min),
        payroll_chain_gap_enabled: g.PAYROLL_CHAIN_GAP_ENABLED !== false,
        payroll_chain_gap_max_min: Number(g.PAYROLL_CHAIN_GAP_MAX_MIN ?? DEFAULT_SERVICE_DURATION_RULES.payroll_chain_gap_max_min),
            service_min_shortfall_percent: Number(g.SERVICE_MIN_SHORTFALL_PERCENT ?? DEFAULT_SERVICE_DURATION_RULES.service_min_shortfall_percent),
            service_max_excess_percent: Number(g.SERVICE_MAX_EXCESS_PERCENT ?? DEFAULT_SERVICE_DURATION_RULES.service_max_excess_percent),
            gps_route_max_excess_percent: Number(g.GPS_ROUTE_MAX_EXCESS_PERCENT ?? DEFAULT_SERVICE_DURATION_RULES.gps_route_max_excess_percent),

        ei_fixed_min: Number(eiFixed || DEFAULT_SERVICE_DURATION_RULES.ei_fixed_min),
        pi_fixed_min: Number(piFixed || DEFAULT_SERVICE_DURATION_RULES.pi_fixed_min),
        st_fixed_min: Number(stFixed || DEFAULT_SERVICE_DURATION_RULES.st_fixed_min),

        ei_default_min: Number(eiDef || DEFAULT_SERVICE_DURATION_RULES.ei_default_min),
        ei_min: Number(eiMin || DEFAULT_SERVICE_DURATION_RULES.ei_min),
        ei_max: Number(eiMax || DEFAULT_SERVICE_DURATION_RULES.ei_max),

        pi_default_min: Number(piDef || DEFAULT_SERVICE_DURATION_RULES.pi_default_min),
        pi_min: Number(piMin || DEFAULT_SERVICE_DURATION_RULES.pi_min),
        pi_max: Number(piMax || DEFAULT_SERVICE_DURATION_RULES.pi_max),

        st_default_min: Number(stDef || DEFAULT_SERVICE_DURATION_RULES.st_default_min),
        st_min: Number(stMin || DEFAULT_SERVICE_DURATION_RULES.st_min),
        st_max: Number(stMax || DEFAULT_SERVICE_DURATION_RULES.st_max),

        aba_default_min: Number(ABA.default ?? DEFAULT_SERVICE_DURATION_RULES.aba_default_min),
        aba_min: Number(ABA.min ?? DEFAULT_SERVICE_DURATION_RULES.aba_min),
        aba_max: Number(ABA.max ?? DEFAULT_SERVICE_DURATION_RULES.aba_max),

        otpt_default_min: Number(OT.default ?? DEFAULT_SERVICE_DURATION_RULES.otpt_default_min),
        otpt_min: Number(OT.min ?? DEFAULT_SERVICE_DURATION_RULES.otpt_min),
        otpt_max: Number(OT.max ?? DEFAULT_SERVICE_DURATION_RULES.otpt_max),

        event_default_min: Number(EVENT.default ?? DEFAULT_SERVICE_DURATION_RULES.event_default_min),
        event_min: Number(EVENT.min ?? DEFAULT_SERVICE_DURATION_RULES.event_min),
        event_max: Number(EVENT.max ?? DEFAULT_SERVICE_DURATION_RULES.event_max),
      });

      flash('Service duration rules updated!');
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to save service rules');
    } finally {
      setSavingServiceRules(false);
    }
  };

  const handleMentorFeeChange = (field, value) => {
    setMentorFees((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveMentorFees = async () => {
    setSavingMentorFees(true);
    try {
      const num = (v) => {
        const n = Number(v);
        if (!Number.isFinite(n) || n < 0) return 0;
        return Math.round(n * 100) / 100;
      };

      const payload = {
        EI: num(mentorFees.ei),
        PI: num(mentorFees.pi),
        ABA: num(mentorFees.aba),
        OT: num(mentorFees.ot),
        PT: num(mentorFees.pt),
        ST: num(mentorFees.st),
      };

      const res = await API.post('/mentor-fee-settings', payload);
      const merged = res.data || payload;

      setMentorFees({
        ei: Number(merged.EI ?? merged.ei ?? payload.EI) || 0,
        pi: Number(merged.PI ?? merged.pi ?? payload.PI) || 0,
        aba: Number(merged.ABA ?? merged.aba ?? payload.ABA) || 0,
        ot: Number(merged.OT ?? merged.ot ?? payload.OT) || 0,
        pt: Number(merged.PT ?? merged.pt ?? payload.PT) || 0,
        st: Number(merged.ST ?? merged.st ?? payload.ST) || 0,
      });

      flash('Mentorship fees updated!');
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to save mentorship fees');
    } finally {
      setSavingMentorFees(false);
    }
  };

  const savePayrollEvvSettings = async () => {
    setSavingPayrollEvvSettings(true);
    try {
      const payload = {
        require_evv_complete_for_payroll: !!payrollEvvSettings.require_evv_complete_for_payroll,
        apply_only_when_evv_required: true,
      };

      const res = await API.post('/admin/payroll-evv-settings', payload);
      const saved = res.data || payload;

      setPayrollEvvSettings({
        require_evv_complete_for_payroll: !!saved.require_evv_complete_for_payroll,
        apply_only_when_evv_required: true,
      });

      flash('Payroll EVV validation updated!');
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to save payroll EVV validation settings');
    } finally {
      setSavingPayrollEvvSettings(false);
    }
  };

  const handleTrainingRuleChange = (path, value) => {
    const toInt = (v, fallback) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return fallback;
      return Math.max(0, Math.round(n));
    };

    setTrainingRules((prev) => {
      const next = JSON.parse(JSON.stringify(prev || DEFAULT_TRAINING_RULES));
      const parts = String(path).split('.');
      if (parts.length === 1) {
        const key = parts[0];
        const fallback = DEFAULT_TRAINING_RULES[key];
        next[key] = toInt(value, fallback);
        return next;
      }
      if (parts.length === 2) {
        const [grp, key] = parts;
        const fallback = DEFAULT_TRAINING_RULES?.[grp]?.[key];
        if (!next[grp]) next[grp] = {};
        next[grp][key] = toInt(value, fallback);
        return next;
      }
      return next;
    });
  };

  const saveTrainingRules = async () => {
    setSavingTrainingRules(true);
    try {
      const norm = (v, def) => {
        const n = Number(v);
        if (!Number.isFinite(n)) return def;
        return Math.max(0, Math.round(n));
      };

      const payload = {
        shadow: {
          min_visits: norm(trainingRules?.shadow?.min_visits, DEFAULT_TRAINING_RULES.shadow.min_visits),
          min_days: norm(trainingRules?.shadow?.min_days, DEFAULT_TRAINING_RULES.shadow.min_days),
        },
        supervised: {
          min_visits: norm(trainingRules?.supervised?.min_visits, DEFAULT_TRAINING_RULES.supervised.min_visits),
          min_days: norm(trainingRules?.supervised?.min_days, DEFAULT_TRAINING_RULES.supervised.min_days),
        },
        min_monthly_visits: norm(trainingRules?.min_monthly_visits, DEFAULT_TRAINING_RULES.min_monthly_visits),
        stuck_after_days: norm(trainingRules?.stuck_after_days, DEFAULT_TRAINING_RULES.stuck_after_days),
      };

      const res = await API.post('/training-rules-settings', payload);
      const merged = res.data || payload;

      setTrainingRules({
        shadow: {
          min_visits: Number(merged?.shadow?.min_visits ?? payload.shadow.min_visits) || payload.shadow.min_visits,
          min_days: Number(merged?.shadow?.min_days ?? payload.shadow.min_days) || payload.shadow.min_days,
        },
        supervised: {
          min_visits: Number(merged?.supervised?.min_visits ?? payload.supervised.min_visits) || payload.supervised.min_visits,
          min_days: Number(merged?.supervised?.min_days ?? payload.supervised.min_days) || payload.supervised.min_days,
        },
        min_monthly_visits: Number(merged?.min_monthly_visits ?? payload.min_monthly_visits) || payload.min_monthly_visits,
        stuck_after_days: Number(merged?.stuck_after_days ?? payload.stuck_after_days) || payload.stuck_after_days,
      });

      flash('Training rules updated!');
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to save training rules');
    } finally {
      setSavingTrainingRules(false);
    }
  };

  const saveSecuritySettings = async () => {
    setSavingSecuritySettings(true);
    try {
      const payload = {
        require_2fa_admin: !!securitySettings.require_2fa_admin,
        require_2fa_teacher: !!securitySettings.require_2fa_teacher,
        allow_trusted_devices: !!securitySettings.allow_trusted_devices,
        trusted_device_days: Math.max(
          1,
          Math.min(365, Number(securitySettings.trusted_device_days || 30))
        ),
      };

      await API.post('/admin/security-settings', payload);
      flash('Security settings updated!');
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || 'Failed to save security settings');
    } finally {
      setSavingSecuritySettings(false);
    }
  };

  const saveSmartTripAutomationSettings = async () => {
    setSavingSmartTripAutomation(true);
    try {
      const autopilotAvailable = !!smartTripAutomationSettings.autopilot_available;
      const payload = {
        smart_assist_enabled: true,
        autopilot_available: autopilotAvailable,
        allow_user_opt_in: autopilotAvailable
          ? !!smartTripAutomationSettings.allow_user_opt_in
          : false,
      };

      const res = await API.post('/admin/smart-trip-automation-settings', payload);
      const saved = res.data || payload;
      const savedAutopilotAvailable = !!saved.autopilot_available;

      setSmartTripAutomationSettings({
        ...DEFAULT_SMART_TRIP_AUTOMATION_SETTINGS,
        smart_assist_enabled: saved.smart_assist_enabled !== false,
        autopilot_available: savedAutopilotAvailable,
        allow_user_opt_in: savedAutopilotAvailable ? !!saved.allow_user_opt_in : false,
      });

      flash('Smart Trip Automation settings updated!');
    } catch (e) {
      console.error(e);
      alert(
        e?.response?.data?.error ||
          e?.response?.data?.message ||
          'Failed to save Smart Trip Automation settings'
      );
    } finally {
      setSavingSmartTripAutomation(false);
    }
  };

  const logoSrc = brandLogoUrl
    ? brandLogoUrl.includes('?')
      ? `${brandLogoUrl}&v=${brandLogoVer}`
      : `${brandLogoUrl}?v=${brandLogoVer}`
    : '';

  const formatMoney = (v) => Number(v || 0).toFixed(2);

  const getInvoicePdfUrl = (inv) => {
    if (!inv.pdf_path) return null;
    const idx = inv.pdf_path.indexOf('/documents/invoices/');
    if (idx !== -1) return inv.pdf_path.slice(idx);
    return inv.pdf_path;
  };

  const openMailingModal = async () => {
    setMailingModalOpen(true);
    setMailingLoading(true);
    try {
      const res = await API.get('/parent-mailing-list');
      setMailingParents(res.data || []);
    } catch (e) {
      console.error(e);
      alert('Failed to load parent mailing list');
      setMailingModalOpen(false);
    } finally {
      setMailingLoading(false);
    }
  };

  const toggleParentMailing = (parentId) => {
    setMailingParents((prev) =>
      prev.map((p) =>
        p.parent_id === parentId
          ? { ...p, receive_visit_notifications: p.receive_visit_notifications ? 0 : 1 }
          : p
      )
    );
  };

  const saveMailingModal = async () => {
    setMailingSaving(true);
    try {
      await API.post('/parent-mailing-list', mailingParents);
      flash('Parent mailing list updated!');
      setMailingModalOpen(false);
    } catch (e) {
      console.error(e);
      alert('Failed to save parent mailing list');
    } finally {
      setMailingSaving(false);
    }
  };

  const savePassword = async () => {
    setPasswordError('');

    if (!newPassword || newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }

    setSavingPassword(true);
    try {
      await API.post('/me/password', {
        current_password: currentPassword || '',
        new_password: newPassword,
      });

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      flash(hasPassword ? 'Password updated.' : 'Password set successfully.');
      setHasPassword(true);
    } catch (e) {
      const msg = e?.response?.data?.error || 'Failed to update password.';
      setPasswordError(msg);
    } finally {
      setSavingPassword(false);
    }
  };

  const isFirstPassword = hasPassword === false;

  const invoiceStatusBadgeClass = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-emerald-100 text-emerald-700';
      case 'overdue':
        return 'bg-red-100 text-red-700';
      case 'sent':
        return 'bg-sky-100 text-sky-700';
      case 'canceled':
        return 'bg-gray-100 text-gray-500';
      default:
        return 'bg-amber-100 text-amber-700';
    }
  };

  const renderStudentsForMailing = (p) => {
    const v =
      p.students ||
      p.students_text ||
      p.students_list ||
      p.student_names ||
      p.students_names ||
      null;

    if (Array.isArray(v)) return v.filter(Boolean).join(', ');
    if (typeof v === 'string') return v;
    if (typeof p.students_count === 'number') return `${p.students_count} student(s)`;
    return '';
  };

  return (
    <>
      {/* NEW: centered SaaS toast (white, readable, desktop+mobile) */}
      {successMsg && (
        <div className="fixed inset-0 z-[9999] pointer-events-none flex items-center justify-center px-3">
          <div className="absolute inset-0 bg-black/20" />
          <div className="relative pointer-events-auto w-full max-w-sm bg-white border border-slate-200 rounded-xl shadow-xl px-4 py-3 text-sm text-slate-800">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-2">
                <div className="mt-0.5 w-2 h-2 rounded-full bg-emerald-500" />
                <div className="leading-snug">{successMsg}</div>
              </div>
              <button
                type="button"
                className="text-xs text-slate-400 hover:text-slate-600"
                onClick={() => setSuccessMsg('')}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="w-full max-w-4xl mx-auto bg-white p-1 rounded shadow mt-1 space-y-6">
        <div className="text-center mb-2">
          <h2 className="text-xl font-bold text-blue-800">System Settings</h2>
          <p className="text-xs text-gray-500">
            Branding, timezone, visit rules &amp; parent email notifications.
          </p>
        </div>

        <nav
          aria-label="Settings sections"
          className="hidden lg:block rounded-lg border border-slate-200 bg-slate-50/70 p-3"
        >
          <p className="mb-2 text-xs font-semibold text-slate-600">Jump to section</p>
          <div className="grid grid-cols-4 gap-2">
            <a
              href="#settings-organization"
              title="Branding and clinic locations"
              className="flex min-h-[44px] items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              <Palette size={16} className="shrink-0" aria-hidden="true" />
              <span>Organization</span>
            </a>
            <a
              href="#settings-scheduling"
              title="Timezone and default workday"
              className="flex min-h-[44px] items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              <CalendarClock size={16} className="shrink-0" aria-hidden="true" />
              <span>Scheduling</span>
            </a>
            <a
              href="#settings-training"
              title="Training rules and mentorship fees"
              className="flex min-h-[44px] items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              <GraduationCap size={16} className="shrink-0" aria-hidden="true" />
              <span>Training</span>
            </a>
            <a
              href="#settings-communication"
              title="Parent visit confirmations"
              className="flex min-h-[44px] items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              <MailCheck size={16} className="shrink-0" aria-hidden="true" />
              <span>Communication</span>
            </a>
            <a
              href="#settings-payroll"
              title="Verification and service calculation rules"
              className="flex min-h-[44px] items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              <Calculator size={16} className="shrink-0" aria-hidden="true" />
              <span>Payroll &amp; EVV</span>
            </a>
            <a
              href="#settings-automation"
              title="Smart Trip and AutoPilot settings"
              className="flex min-h-[44px] items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              <Route size={16} className="shrink-0" aria-hidden="true" />
              <span>Trip automation</span>
            </a>
            <a
              href="#settings-security"
              title="Organization login and account security"
              className="flex min-h-[44px] items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              <ShieldCheck size={16} className="shrink-0" aria-hidden="true" />
              <span>Security</span>
            </a>
            <a
              href="#settings-billing"
              title="Subscription invoices"
              className="flex min-h-[44px] items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              <FileText size={16} className="shrink-0" aria-hidden="true" />
              <span>Billing</span>
            </a>
          </div>
        </nav>

        {/* REMOVED: old inline success banner */}
        {/* {successMsg && (
          <div className="mx-2 -mt-2 text-[12px] bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-3 py-2">
            {successMsg}
          </div>
        )} */}

        {/* BRANDING (name + logo) */}
        <div id="settings-organization" className="scroll-mt-40 border border-blue-100 rounded-lg p-4 bg-blue-50/40">
          <SettingsSectionHeading
                icon={Palette}
                iconClassName="bg-blue-100 text-blue-700"
                className="font-semibold mb-3 text-sm text-blue-900"
              >
                Branding (name &amp; logo)
              </SettingsSectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-[2fr,1fr] gap-4 items-center">
            <div>
              <p className="text-xs text-gray-600 mb-1">
                This name will be used in all outgoing emails and reports.
              </p>
              <input
                type="text"
                className="border p-2 rounded w-full"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="e.g. EarlyCare, TIP Infant Services..."
                disabled={loading || savingBrand}
              />
              <button
                className={`mt-2 bg-blue-600 text-white px-4 py-2 rounded text-sm ${
                  savingBrand ? 'opacity-50 pointer-events-none' : ''
                }`}
                onClick={saveBrand}
                disabled={savingBrand}
              >
                {savingBrand ? 'Saving...' : 'Save brand name'}
              </button>
            </div>

            <div className="flex flex-col items-center md:items-start gap-2">
              <div className="w-32 h-16 border border-dashed border-blue-300 bg-white rounded flex items-center justify-center overflow-hidden">
                {brandLogoUrl ? (
                  <img
                    src={logoSrc}
                    alt="Brand logo"
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <span className="text-[10px] text-blue-400 text-center px-2">
                    No logo uploaded
                  </span>
                )}
              </div>
              <p className="text-[11px] text-gray-500 text-center md:text-left">
                PNG or JPG, preferably horizontal. Used on Student Progress and Teacher Work reports.
              </p>
              <input
                type="file"
                accept="image/png,image/jpeg"
                onChange={handleLogoFileChange}
                className="text-xs"
                disabled={uploadingLogo || loading}
              />
              <button
                type="button"
                className={`mt-1 bg-blue-600 text-white px-3 py-1.5 rounded text-xs ${
                  uploadingLogo ? 'opacity-50 pointer-events-none' : ''
                }`}
                onClick={saveBrandLogo}
                disabled={uploadingLogo}
              >
                {uploadingLogo ? 'Uploading...' : 'Upload logo'}
              </button>
            </div>
          </div>
        </div>

        {/* CLINIC SETTINGS */}
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/60">
          <SettingsSectionHeading
                icon={Building2}
                iconClassName="bg-slate-100 text-slate-700"
                className="font-semibold mb-2 text-sm text-slate-900"
              >
                Clinic (optional)
              </SettingsSectionHeading>
          <p className="text-xs text-gray-700 mb-2">
            If your organization also provides in-clinic sessions or schedules clinic meetings/training events,
            define one or more clinic locations here. Pick a <b>Primary</b> location to be used as the default address for “At clinic” visits/events.
          </p>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!clinicEnabled}
                onChange={(e) => setClinicEnabled(e.target.checked)}
                disabled={savingClinicMeta || loading}
              />
              <span className="text-sm text-gray-700">
                Enable clinic locations for this organization
              </span>
            </label>

            <div className="flex items-center gap-2">
              <button
                type="button"
                className="bg-white border border-slate-300 text-slate-700 px-3 py-1.5 rounded text-xs hover:bg-slate-100"
                onClick={loadOrgLocations}
                disabled={orgLocationsLoading || loading}
                title="Reload locations"
              >
                {orgLocationsLoading ? 'Loading…' : 'Refresh'}
              </button>

              <button
                type="button"
                className="bg-slate-800 text-white px-3 py-1.5 rounded text-xs hover:bg-slate-900"
                onClick={openAddLocation}
                disabled={!clinicEnabled || loading}
              >
                + Add location
              </button>
            </div>
          </div>

          <div className={'grid grid-cols-1 md:grid-cols-6 gap-2 ' + (!clinicEnabled ? 'opacity-60' : '')}>
            <div className="md:col-span-6">
              <label className="block text-[11px] text-gray-700 mb-0.5">
                Clinic display name (optional)
              </label>
              <input
                type="text"
                className="border px-2 py-1 rounded w-full text-xs"
                value={clinicDisplayName}
                onChange={(e) => setClinicDisplayName(e.target.value)}
                placeholder="e.g. ABC Clinic"
                disabled={!clinicEnabled || savingClinicMeta || loading}
              />
              <p className="text-[10px] text-gray-500 mt-1">
                This is a label for your clinic context (not a physical address). Physical clinic addresses are managed below as locations.
              </p>
            </div>

            <div className="md:col-span-6">
              <label className="block text-[11px] text-gray-700 mb-0.5">
                Primary clinic location
              </label>
              <select
                className="border px-2 py-1 rounded w-full text-xs bg-white"
                value={clinicDefaultLocationId || ''}
                onChange={(e) => setClinicDefaultLocationId(e.target.value ? Number(e.target.value) : null)}
                disabled={!clinicEnabled || savingClinicMeta || loading || orgLocationsLoading}
              >
                <option value="">-- Choose primary location --</option>
                {orgLocations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                    {formatLocationAddr(l) ? ` — ${formatLocationAddr(l)}` : ''}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-gray-500 mt-1">
                You can also click “Set primary” from the locations table.
              </p>
            </div>
          </div>

          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <button
              className={`bg-slate-800 text-white px-4 py-2 rounded text-sm ${
                savingClinicMeta ? 'opacity-50 pointer-events-none' : ''
              }`}
              onClick={saveClinicMeta}
              disabled={savingClinicMeta}
            >
              {savingClinicMeta ? 'Saving...' : 'Save clinic settings'}
            </button>

            {clinicEnabled && primaryLocation && (
              <div className="text-[11px] text-gray-600">
                <div className="font-semibold text-gray-700">
                  Primary: {primaryLocation.name}
                </div>
                {formatLocationAddr(primaryLocation) && (
                  <div>{formatLocationAddr(primaryLocation)}</div>
                )}
                {primaryLocation.latitude != null && primaryLocation.longitude != null && (
                  <div>
                    <b>Lat/Lon:</b> {primaryLocation.latitude}, {primaryLocation.longitude}
                  </div>
                )}
                {primaryLocation.geocoded_label && (
                  <div>
                    <b>Geocoded:</b> {primaryLocation.geocoded_label}
                  </div>
                )}
              </div>
            )}
          </div>

          {!clinicEnabled && (
            <p className="mt-2 text-[11px] text-gray-500">
              Clinic is disabled — the system will behave as a home-visit-first workflow unless a specific visit/event is marked “At clinic”.
            </p>
          )}

          <div className="mt-3 border border-slate-200 rounded-md bg-white overflow-hidden">
            <div className="px-2 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <div className="text-xs font-semibold text-slate-800">
                Active locations ({orgLocations.length}/10)
              </div>
              <div className="text-[10px] text-slate-500">
                Deactivate removes it from selection (soft delete).
              </div>
            </div>

            {orgLocationsLoading ? (
              <div className="p-2 text-xs text-gray-600">Loading locations…</div>
            ) : orgLocations.length === 0 ? (
              <div className="p-2 text-xs text-gray-500">
                No active locations yet. Click <b>+ Add location</b>.
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-white">
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-2 py-1">Name</th>
                    <th className="text-left px-2 py-1">Address</th>
                    <th className="text-left px-2 py-1">Geo</th>
                    <th className="text-right px-2 py-1">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orgLocations.map((l) => {
                    const isPrimary = Number(l.id) === Number(clinicDefaultLocationId);
                    const addr = formatLocationAddr(l);
                    const geoOk = l.latitude != null && l.longitude != null;
                    return (
                      <tr key={l.id} className="border-t border-slate-50">
                        <td className="px-2 py-1 align-top">
                          <div className="font-semibold text-gray-800 flex items-center gap-2">
                            <span>{l.name}</span>
                            {isPrimary && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] bg-emerald-100 text-emerald-800">
                                Primary
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-gray-400">ID: {l.id}</div>
                        </td>

                        <td className="px-2 py-1 align-top text-gray-700">
                          {addr || <span className="text-[10px] text-gray-400">No address</span>}
                        </td>

                        <td className="px-2 py-1 align-top text-gray-700">
                          {geoOk ? (
                            <>
                              <div className="text-[10px]">
                                {l.latitude}, {l.longitude}
                              </div>
                              {l.geocoded_label && (
                                <div className="text-[10px] text-gray-500">
                                  {l.geocoded_label}
                                </div>
                              )}
                            </>
                          ) : (
                            <span className="text-[10px] text-gray-400">Not geocoded</span>
                          )}
                        </td>

                        <td className="px-2 py-1 align-top text-right">
                          <div className="inline-flex flex-wrap gap-1 justify-end">
                            <button
                              type="button"
                              className="px-2 py-1 rounded border text-[10px] bg-white hover:bg-slate-50"
                              onClick={() => openEditLocation(l)}
                              disabled={!clinicEnabled || loading}
                            >
                              Edit
                            </button>

                            {!isPrimary && (
                              <button
                                type="button"
                                className="px-2 py-1 rounded border text-[10px] bg-white hover:bg-slate-50"
                                onClick={() => setPrimaryLocation(l.id)}
                                disabled={!clinicEnabled || loading}
                                title="Set as primary"
                              >
                                Set primary
                              </button>
                            )}

                            <button
                              type="button"
                              className="px-2 py-1 rounded border text-[10px] bg-white hover:bg-red-50 border-red-200 text-red-700"
                              onClick={() => deactivateLocation(l.id)}
                              disabled={!clinicEnabled || loading}
                            >
                              Deactivate
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* TIMEZONE + WORKDAY */}
        <div id="settings-scheduling" className="scroll-mt-40 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <SettingsSectionHeading
                icon={Globe2}
                iconClassName="bg-gray-100 text-gray-700"
                className="font-semibold mb-2 text-sm text-gray-900"
              >
                Timezone
              </SettingsSectionHeading>
            <select
              className="border p-2 rounded w-full mb-3 text-sm"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              disabled={loading}
            >
              <option value="">-- Choose timezone --</option>
              {timezones.map((z) => (
                <option key={z.id} value={z.name}>
                  {z.name}
                </option>
              ))}
            </select>
            <button
              className={`bg-blue-600 text-white px-4 py-2 rounded text-sm ${
                !selected || savingTz ? 'opacity-50 pointer-events-none' : ''
              }`}
              onClick={saveTimezone}
              disabled={!selected || savingTz}
            >
              {savingTz ? 'Saving...' : 'Save timezone'}
            </button>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <SettingsSectionHeading
                icon={CalendarClock}
                iconClassName="bg-gray-100 text-gray-700"
                className="font-semibold mb-2 text-sm text-gray-900"
              >
                Default workday
              </SettingsSectionHeading>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Start</label>
                <input
                  type="time"
                  className="border p-2 rounded w-full text-sm"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">End</label>
                <input
                  type="time"
                  className="border p-2 rounded w-full text-sm"
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>
            <button
              className={`mt-3 bg-blue-600 text-white px-4 py-2 rounded text-sm ${
                savingWd ? 'opacity-50 pointer-events-none' : ''
              }`}
              onClick={saveWorkday}
              disabled={savingWd}
            >
              {savingWd ? 'Saving...' : 'Save workday'}
            </button>
          </div>
        </div>

        {/* TRAINING RULES */}
        <div id="settings-training" className="scroll-mt-40 border border-teal-200 rounded-lg p-4 bg-teal-50/70">
          <SettingsSectionHeading
                icon={GraduationCap}
                iconClassName="bg-teal-100 text-teal-700"
                className="font-semibold mb-2 text-sm text-teal-900"
              >
                Training rules
              </SettingsSectionHeading>
          <p className="text-xs text-gray-700 mb-2">
            These rules control trainee progression (Shadow → Supervised) and detection of “stuck” trainees.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            <div className="border border-teal-100 bg-white rounded-md p-3">
              <h4 className="font-semibold text-teal-900 mb-2">Shadow stage</h4>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block mb-1">Min visits</span>
                  <input
                    type="number"
                    min={0}
                    className="border p-1 rounded w-full"
                    value={trainingRules.shadow.min_visits}
                    onChange={(e) => handleTrainingRuleChange('shadow.min_visits', e.target.value)}
                    disabled={loading || savingTrainingRules}
                  />
                </label>
                <label className="block">
                  <span className="block mb-1">Min days</span>
                  <input
                    type="number"
                    min={0}
                    className="border p-1 rounded w-full"
                    value={trainingRules.shadow.min_days}
                    onChange={(e) => handleTrainingRuleChange('shadow.min_days', e.target.value)}
                    disabled={loading || savingTrainingRules}
                  />
                </label>
              </div>
              <p className="mt-2 text-[10px] text-gray-500">
                Trainee can exit Shadow only after meeting both thresholds (visits + days).
              </p>
            </div>

            <div className="border border-teal-100 bg-white rounded-md p-3">
              <h4 className="font-semibold text-teal-900 mb-2">Supervised stage</h4>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block mb-1">Min visits</span>
                  <input
                    type="number"
                    min={0}
                    className="border p-1 rounded w-full"
                    value={trainingRules.supervised.min_visits}
                    onChange={(e) => handleTrainingRuleChange('supervised.min_visits', e.target.value)}
                    disabled={loading || savingTrainingRules}
                  />
                </label>
                <label className="block">
                  <span className="block mb-1">Min days</span>
                  <input
                    type="number"
                    min={0}
                    className="border p-1 rounded w-full"
                    value={trainingRules.supervised.min_days}
                    onChange={(e) => handleTrainingRuleChange('supervised.min_days', e.target.value)}
                    disabled={loading || savingTrainingRules}
                  />
                </label>
              </div>
              <p className="mt-2 text-[10px] text-gray-500">
                Trainee can exit Supervised only after meeting both thresholds.
              </p>
            </div>

            <div className="border border-teal-100 bg-white rounded-md p-3 md:col-span-2">
              <h4 className="font-semibold text-teal-900 mb-2">Quality guardrails</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <span className="block mb-1">Min monthly visits</span>
                  <input
                    type="number"
                    min={0}
                    className="border p-1 rounded w-full"
                    value={trainingRules.min_monthly_visits}
                    onChange={(e) => handleTrainingRuleChange('min_monthly_visits', e.target.value)}
                    disabled={loading || savingTrainingRules}
                  />
                  <span className="text-[10px] text-gray-500">
                    If trainee logs fewer visits per month, they may be considered “inactive / not progressing”.
                  </span>
                </label>

                <label className="block">
                  <span className="block mb-1">Stuck after days</span>
                  <input
                    type="number"
                    min={0}
                    className="border p-1 rounded w-full"
                    value={trainingRules.stuck_after_days}
                    onChange={(e) => handleTrainingRuleChange('stuck_after_days', e.target.value)}
                    disabled={loading || savingTrainingRules}
                  />
                  <span className="text-[10px] text-gray-500">
                    If trainee does not progress after this many days in a stage, flag them as “stuck”.
                  </span>
                </label>
              </div>
            </div>
          </div>

          <button
            className={`mt-3 bg-teal-700 text-white px-4 py-2 rounded text-sm ${
              savingTrainingRules ? 'opacity-50 pointer-events-none' : ''
            }`}
            onClick={saveTrainingRules}
            disabled={savingTrainingRules}
          >
            {savingTrainingRules ? 'Saving...' : 'Save training rules'}
          </button>
        </div>

        {/* MENTORSHIP FEES */}
        <div className="border border-sky-200 rounded-lg p-4 bg-sky-50/70">
          <SettingsSectionHeading
                icon={HandCoins}
                iconClassName="bg-sky-100 text-sky-700"
                className="font-semibold mb-2 text-sm text-sky-900"
              >
                Mentorship fee (per visit)
              </SettingsSectionHeading>
          <p className="text-xs text-gray-700 mb-2">
            Extra amount paid to a mentor teacher when a visit is marked as a mentorship/training visit.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 text-xs">
            {['ei', 'pi', 'aba', 'ot', 'pt', 'st'].map((k) => (
              <label key={k} className="block">
                <span className="block mb-1 font-semibold text-sky-900">{k.toUpperCase()}</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="border p-1 rounded w-full"
                  value={mentorFees[k]}
                  onChange={(e) => handleMentorFeeChange(k, e.target.value)}
                />
                <span className="text-[10px] text-gray-500">USD per {k.toUpperCase()} visit</span>
              </label>
            ))}
          </div>

          <button
            className={`mt-3 bg-sky-700 text-white px-4 py-2 rounded text-sm ${
              savingMentorFees ? 'opacity-50 pointer-events-none' : ''
            }`}
            onClick={saveMentorFees}
            disabled={savingMentorFees}
          >
            {savingMentorFees ? 'Saving...' : 'Save mentorship fees'}
          </button>
        </div>

        {/* Parent notifications */}
        <div id="settings-communication" className="scroll-mt-40 border border-green-200 rounded-lg p-4 bg-green-50/60">
          <SettingsSectionHeading
                icon={MailCheck}
                iconClassName="bg-green-100 text-green-700"
                className="font-semibold mb-2 text-sm text-green-900"
              >
                Parent visit confirmations
              </SettingsSectionHeading>

          <label className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              checked={parentNotifEnabled}
              onChange={(e) => setParentNotifEnabled(e.target.checked)}
              disabled={savingParentNotif || loading}
            />
            <span className="text-sm text-gray-700">
              If enabled, parents/guardians with an email address will receive reminder emails 24 hours and 1 hour
              before scheduled visits.
            </span>
          </label>

          <div className="ml-6 mt-2 space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={parentNotifIncludeActions}
                onChange={(e) => setParentNotifIncludeActions(e.target.checked)}
                disabled={savingParentNotif || loading}
              />
              <span className="text-sm text-gray-700">
                Include <b>Accept</b> / <b>Cancel</b> buttons in parent emails.
              </span>
            </label>

            <div>
              <label className="block text-sm text-gray-700 mb-1">
                Additional note for parents (optional)
              </label>
              <textarea
                className="border p-2 rounded w-full text-sm"
                rows={4}
                value={parentNotifExtraMessage}
                onChange={(e) => setParentNotifExtraMessage(e.target.value)}
                placeholder="E.g. contact details, rescheduling policy, or a general note for families..."
                disabled={savingParentNotif || loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                This note will be added near the bottom of all parent reminder emails.
              </p>
            </div>
          </div>

          <button
            className={`mt-3 bg-green-700 text-white px-4 py-2 rounded text-sm ${
              savingParentNotif ? 'opacity-50 pointer-events-none' : ''
            }`}
            onClick={saveParentNotif}
            disabled={savingParentNotif}
          >
            {savingParentNotif ? 'Saving...' : 'Save parent notifications'}
          </button>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-[11px] text-gray-600">
              Control which parents receive visit reminder emails.
            </p>
            <button
              type="button"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white text-green-800 border border-green-500 shadow-sm text-[11px] hover:bg-green-600 hover:text-white transition-colors"
              onClick={openMailingModal}
            >
              <Mail size={12} />
              Manage parent email list (opt-in/out)
            </button>
          </div>

          <div className="mt-4 flex items-center gap-2 text-xs text-gray-700">
            <span className="font-semibold">Preview reminder type:</span>
            <button
              type="button"
              onClick={() => setPreviewType('24h')}
              className={
                'px-3 py-1 rounded border text-xs ' +
                (previewType === '24h'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300')
              }
            >
              24h
            </button>
            <button
              type="button"
              onClick={() => setPreviewType('1h')}
              className={
                'px-3 py-1 rounded border text-xs ' +
                (previewType === '1h'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300')
              }
            >
              1h
            </button>
          </div>

          <ParentEmailPreview
            brandName={brandName}
            timezone={selected}
            includeActions={parentNotifIncludeActions}
            extraMessage={parentNotifExtraMessage}
            enabled={parentNotifEnabled}
            reminderType={previewType}
          />
        </div>

        {/* EVV TOLERANCES — independent of payroll calculation settings */}
        <section className="rounded-lg border border-sky-200 bg-sky-50/60 p-4" aria-labelledby="evv-tolerances-title" aria-busy={loadingEvvTolerances || savingEvvTolerances}>
          <div id="evv-tolerances-title">
            <SettingsSectionHeading icon={BadgeCheck} iconClassName="bg-sky-100 text-sky-700" className="mb-2 text-sm font-semibold text-sky-900">
              EVV verification tolerances
            </SettingsSectionHeading>
          </div>
          <p className="mb-4 max-w-3xl text-xs text-gray-700">Control the EVV service-duration tolerance and the allowed distance of Check In / Out coordinates from the expected location. These settings apply to the existing EVV checks for recorded visit duration and action locations.</p>
          {evvTolerancesError && <div role="alert" className="mb-3 rounded border border-red-200 bg-red-50 p-3 text-xs text-red-800">{evvTolerancesError}{!evvTolerancesLoaded && <button type="button" disabled={loadingEvvTolerances} onClick={() => setEvvTolerancesLoadAttempt(v => v + 1)} className="ml-3 underline">Retry loading</button>}</div>}
          {loadingEvvTolerances && <p role="status" className="mb-3 text-xs text-slate-600">Loading EVV tolerances…</p>}
          <fieldset disabled={!evvTolerancesLoaded || loadingEvvTolerances || savingEvvTolerances}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="block text-xs font-medium text-slate-800">Time tolerance (minutes)
                <input type="number" min="0" max="120" step="1" value={evvTolerances.time_tolerance_minutes} onChange={e => setEvvTolerances(v => ({ ...v, time_tolerance_minutes: e.target.value }))} aria-describedby="evv-time-help" aria-invalid={evvTolerancesLoaded && !evvToleranceInputValid(evvTolerances.time_tolerance_minutes, 0, 120)} className="mt-1 w-full rounded border border-sky-200 bg-white p-2 disabled:opacity-60" />
                <span id="evv-time-help" className="mt-1 block font-normal text-slate-600">Applied below the service minimum and above its maximum. Default: 15 min. Use 0–120 whole minutes; 0 disables the extra tolerance.</span>
              </label>
              <label className="block text-xs font-medium text-slate-800">Location radius (meters)
                <input type="number" min="1" max="100000" step="1" value={evvTolerances.location_radius_meters} onChange={e => setEvvTolerances(v => ({ ...v, location_radius_meters: e.target.value }))} aria-describedby="evv-radius-help" aria-invalid={evvTolerancesLoaded && !evvToleranceInputValid(evvTolerances.location_radius_meters, 1, 100000)} className="mt-1 w-full rounded border border-sky-200 bg-white p-2 disabled:opacity-60" />
                <span id="evv-radius-help" className="mt-1 block font-normal text-slate-600">Maximum allowed GPS distance for both actions. Existing organization radius is preserved; otherwise default: 1,000 m. Use 1–100,000 whole meters.</span>
              </label>
            </div>
          </fieldset>
          <p className="mt-3 text-[11px] text-sky-900">Saved values apply to subsequent EVV evaluations, including AutoPilot. Existing visit statuses are not recalculated automatically.</p>
          <button type="button" onClick={saveEvvTolerances} disabled={!evvTolerancesLoaded || !evvTolerancesValid || loadingEvvTolerances || savingEvvTolerances} className="mt-3 rounded bg-sky-700 px-4 py-2 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50">{savingEvvTolerances ? 'Saving...' : 'Save EVV tolerances'}</button>
        </section>

        {/* PAYROLL EVV VALIDATION */}
        <div id="settings-payroll" className="scroll-mt-40 border border-emerald-200 rounded-lg p-4 bg-emerald-50/60">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
            <div>
              <SettingsSectionHeading
                icon={BadgeCheck}
                iconClassName="bg-emerald-100 text-emerald-700"
                className="font-semibold mb-2 text-sm text-emerald-900"
              >
                Payroll EVV validation
              </SettingsSectionHeading>
              <p className="text-xs text-gray-700 max-w-2xl">
                When enabled, visits that require EVV are included in teacher payroll only after EVV status is complete.
                Visits with EVV exceptions or pending EVV remain out of payroll until an admin resolves them.
              </p>
              <p className="text-[11px] text-emerald-800 mt-2">
                Non-EVV visits and existing confirmed No-show payroll handling remain unchanged.
              </p>
            </div>

            <div className="flex flex-col items-start md:items-end gap-2">
              <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                <input
                  type="checkbox"
                  checked={!!payrollEvvSettings.require_evv_complete_for_payroll}
                  onChange={(e) =>
                    setPayrollEvvSettings((prev) => ({
                      ...prev,
                      require_evv_complete_for_payroll: e.target.checked,
                      apply_only_when_evv_required: true,
                    }))
                  }
                  disabled={loading || savingPayrollEvvSettings}
                />
                <span className="font-semibold">Require completed EVV before payroll</span>
              </label>

              <button
                className={`bg-emerald-700 text-white px-4 py-2 rounded text-sm ${
                  savingPayrollEvvSettings ? 'opacity-50 pointer-events-none' : ''
                }`}
                onClick={savePayrollEvvSettings}
                disabled={savingPayrollEvvSettings}
              >
                {savingPayrollEvvSettings ? 'Saving...' : 'Save EVV payroll setting'}
              </button>
            </div>
          </div>
        </div>

        {/* OBJECTIVE SERVICE RULES */}
        <div className="border border-amber-200 rounded-lg p-4 bg-amber-50/60">
          <SettingsSectionHeading
                icon={Calculator}
                iconClassName="bg-amber-100 text-amber-700"
                className="font-semibold mb-2 text-sm text-amber-900"
              >
                Objective service rules &amp; paid breaks
              </SettingsSectionHeading>
          <p className="text-xs text-gray-700 mb-2">
            Duration rules and tolerances apply to System and Teacher service calculations in work reports.
            Duration values are in minutes; tolerances are percentages. Changes apply when reports are generated again for this organization.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
            <div>
              <h4 className="font-semibold text-amber-900 mb-1">
                Paid breaks &amp; No-show
              </h4>
              <label className="block mb-2">
                <span className="block mb-1">Work block length for paid break</span>
                <input
                  type="number"
                  min={60}
                  className="border p-1 rounded w-full"
                  value={serviceRules.four_hours_block_min}
                  onChange={(e) => handleServiceRuleChange('four_hours_block_min', e.target.value)}
                />
                <span className="text-[10px] text-gray-500">
                  After this many Obj/Subj minutes of work, a paid break is triggered.
                </span>
              </label>

              <label className="block mb-2">
                <span className="block mb-1">Paid break duration</span>
                <input
                  type="number"
                  min={1}
                  className="border p-1 rounded w-full"
                  value={serviceRules.paid_break_min}
                  onChange={(e) => handleServiceRuleChange('paid_break_min', e.target.value)}
                />
                <span className="text-[10px] text-gray-500">
                  Minutes added as paid break per triggered work block.
                </span>
              </label>

              <label className="block">
                <span className="block mb-1">No-show subjective cap</span>
                <input
                  type="number"
                  min={1}
                  className="border p-1 rounded w-full"
                  value={serviceRules.no_show_max_min}
                  onChange={(e) => handleServiceRuleChange('no_show_max_min', e.target.value)}
                />
                <span className="text-[10px] text-gray-500">
                  Max subjective minutes billed for a No-show visit.
                </span>
              </label>

              <div className="mt-4 border-t border-amber-100 pt-3">
                <h5 className="font-semibold text-amber-900 mb-2">
                  Payroll chain gap protection
                </h5>

                <label className="flex items-start gap-2 mb-2">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={serviceRules.payroll_chain_gap_enabled !== false}
                    onChange={(e) =>
                      handleServiceRuleChange('payroll_chain_gap_enabled', e.target.checked)
                    }
                  />
                  <span>
                    <span className="block text-xs font-semibold text-gray-800">
                      Enable long-gap protection
                    </span>
                    <span className="block text-[10px] text-gray-500">
                      Protects payroll from paying GPS/manual travel through long idle gaps.
                    </span>
                  </span>
                </label>

                <label className="block">
                  <span className="block mb-1">Long gap threshold</span>
                  <input
                    type="number"
                    min={30}
                    max={480}
                    className={
                      'border p-1 rounded w-full ' +
                      (serviceRules.payroll_chain_gap_enabled === false ? 'bg-gray-100 text-gray-400' : '')
                    }
                    value={serviceRules.payroll_chain_gap_max_min}
                    onChange={(e) => handleServiceRuleChange('payroll_chain_gap_max_min', e.target.value)}
                    disabled={serviceRules.payroll_chain_gap_enabled === false}
                  />
                  <span className="text-[10px] text-gray-500">
                    Allowed range: 30-480 minutes. Default is 90 minutes.
                  </span>
                </label>
              </div>
            </div>

            <div className="md:col-span-3 border-t border-amber-200 pt-3 md:order-last">
              <h4 className="font-semibold text-amber-900 mb-2">Payroll tolerances</h4>
              <p className="text-[11px] text-gray-600 mb-3">These controls are independent of long-gap protection. Service tolerances apply to both calculations; Work and no-show keep their separate rules. GPS checks run after long-gap policy and before paid-break calculation.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  ['service_min_shortfall_percent', 'Allowed shortfall below service minimum (%)', 100, '50% accepts service from half the configured minimum. Below the lower limit: $0 visit service and no visit bonus, pending review.'],
                  ['service_max_excess_percent', 'Allowed excess above service maximum (%)', 100, '0% means a hard maximum. Within an allowed excess use actual minutes; above it use the discipline maximum.'],
                  ['gps_route_max_excess_percent', 'Maximum GPS deviation from route estimate (%)', 500, 'If GPS distance or duration exceeds this allowance, use the complete Google/ORS estimated route.'],
                ].map(([field, label, max, help]) => <label className="block" key={field}>
                  <span className="block font-medium mb-1">{label}</span>
                  <input type="number" min={0} max={max} step="0.1" value={serviceRules[field]} onChange={(e) => handleServiceRuleChange(field, e.target.value)}
                    aria-invalid={!!serviceToleranceErrors[field]} aria-describedby={`${field}-help`} className={`border p-1 rounded w-full ${serviceToleranceErrors[field] ? 'border-red-500' : ''}`} />
                  <span id={`${field}-help`} className="block text-[10px] text-gray-600 mt-1">{help}</span>
                  {serviceToleranceErrors[field] && <span role="alert" className="block text-[11px] text-red-700 mt-1">{serviceToleranceErrors[field]}</span>}
                </label>)}
              </div>
              <p className="text-[11px] text-amber-900 mt-2">PI example: configured min {serviceRules.pi_min} / max {serviceRules.pi_max} min → accepted from {Number.isFinite(Number(serviceRules.service_min_shortfall_percent)) ? (Number(serviceRules.pi_min) * (100 - Number(serviceRules.service_min_shortfall_percent)) / 100).toFixed(2) : '—'} to {Number.isFinite(Number(serviceRules.service_max_excess_percent)) ? (Number(serviceRules.pi_max) * (100 + Number(serviceRules.service_max_excess_percent)) / 100).toFixed(2) : '—'} min. Outside the upper allowance, cap at {serviceRules.pi_max} min. Original times remain visible.</p>
            </div>

            <div className="md:col-span-2">
              <h4 className="font-semibold text-amber-900 mb-1">
                Service duration rules (default/min/max)
              </h4>

              <div className="bg-white border border-amber-100 rounded-md p-3 space-y-2">
                <Range3
                  label="EI"
                  a={serviceRules.ei_default_min}
                  b={serviceRules.ei_min}
                  c={serviceRules.ei_max}
                  onA={(v) => handleServiceRuleChange('ei_default_min', v)}
                  onB={(v) => handleServiceRuleChange('ei_min', v)}
                  onC={(v) => handleServiceRuleChange('ei_max', v)}
                />
                <Range3
                  label="PI"
                  a={serviceRules.pi_default_min}
                  b={serviceRules.pi_min}
                  c={serviceRules.pi_max}
                  onA={(v) => handleServiceRuleChange('pi_default_min', v)}
                  onB={(v) => handleServiceRuleChange('pi_min', v)}
                  onC={(v) => handleServiceRuleChange('pi_max', v)}
                />
                <Range3
                  label="ST"
                  a={serviceRules.st_default_min}
                  b={serviceRules.st_min}
                  c={serviceRules.st_max}
                  onA={(v) => handleServiceRuleChange('st_default_min', v)}
                  onB={(v) => handleServiceRuleChange('st_min', v)}
                  onC={(v) => handleServiceRuleChange('st_max', v)}
                />

                <div className="h-px bg-amber-50 my-1" />

                <Range3
                  label="ABA"
                  a={serviceRules.aba_default_min}
                  b={serviceRules.aba_min}
                  c={serviceRules.aba_max}
                  onA={(v) => handleServiceRuleChange('aba_default_min', v)}
                  onB={(v) => handleServiceRuleChange('aba_min', v)}
                  onC={(v) => handleServiceRuleChange('aba_max', v)}
                />

                <Range3
                  label="OT/PT"
                  a={serviceRules.otpt_default_min}
                  b={serviceRules.otpt_min}
                  c={serviceRules.otpt_max}
                  onA={(v) => handleServiceRuleChange('otpt_default_min', v)}
                  onB={(v) => handleServiceRuleChange('otpt_min', v)}
                  onC={(v) => handleServiceRuleChange('otpt_max', v)}
                />

                <Range3
                  label="EVENT"
                  a={serviceRules.event_default_min}
                  b={serviceRules.event_min}
                  c={serviceRules.event_max}
                  onA={(v) => handleServiceRuleChange('event_default_min', v)}
                  onB={(v) => handleServiceRuleChange('event_min', v)}
                  onC={(v) => handleServiceRuleChange('event_max', v)}
                />

                <div className="text-[10px] text-gray-500">
                  Notes: durations are clamped to min/max; Event still stays “max one event per student per day”.
                </div>
              </div>
            </div>
          </div>

          <button
            className={`mt-3 bg-amber-700 text-white px-4 py-2 rounded text-sm ${
              savingServiceRules ? 'opacity-50 pointer-events-none' : ''
            }`}
            onClick={saveServiceRules}
            disabled={savingServiceRules}
          >
            {savingServiceRules ? 'Saving...' : 'Save objective rules'}
          </button>
        </div>

        {/* ORGANIZATION LOGIN SECURITY */}
        <div id="settings-security" className="scroll-mt-40 border border-rose-200 rounded-lg p-4 bg-rose-50/60">
          <SettingsSectionHeading
                icon={ShieldCheck}
                iconClassName="bg-rose-100 text-rose-700"
                className="font-semibold mb-2 text-sm text-rose-900"
              >
                Organization login security
              </SettingsSectionHeading>
          <p className="text-xs text-gray-700 mb-3">
            Configure two-factor authentication requirements for users in this organization.
          </p>

          <div className="space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!securitySettings.require_2fa_admin}
                onChange={(e) =>
                  setSecuritySettings((prev) => ({
                    ...prev,
                    require_2fa_admin: e.target.checked,
                  }))
                }
                disabled={securitySettingsLoading || savingSecuritySettings}
              />
              <span className="text-sm text-gray-700">
                Require 2FA for Organization Admins
              </span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!securitySettings.require_2fa_teacher}
                onChange={(e) =>
                  setSecuritySettings((prev) => ({
                    ...prev,
                    require_2fa_teacher: e.target.checked,
                  }))
                }
                disabled={securitySettingsLoading || savingSecuritySettings}
              />
              <span className="text-sm text-gray-700">
                Require 2FA for Teachers
              </span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!securitySettings.allow_trusted_devices}
                onChange={(e) =>
                  setSecuritySettings((prev) => ({
                    ...prev,
                    allow_trusted_devices: e.target.checked,
                  }))
                }
                disabled={securitySettingsLoading || savingSecuritySettings}
              />
              <span className="text-sm text-gray-700">
                Allow trusted devices
              </span>
            </label>

            <div className="max-w-xs">
              <label className="block text-xs text-gray-700 mb-1">
                Trusted device duration (days)
              </label>
              <input
                type="number"
                min={1}
                max={365}
                className="border p-2 rounded w-full text-sm"
                value={securitySettings.trusted_device_days}
                onChange={(e) =>
                  setSecuritySettings((prev) => ({
                    ...prev,
                    trusted_device_days: e.target.value,
                  }))
                }
                disabled={
                  securitySettingsLoading ||
                  savingSecuritySettings ||
                  !securitySettings.allow_trusted_devices
                }
              />
              <p className="text-[11px] text-gray-500 mt-1">
                After this period, users must enter MFA again on that device.
              </p>
            </div>

            <p className="text-[11px] text-gray-500">
              Changes apply to future sign-ins. Already logged-in users will be affected on their next login.
            </p>
          </div>

          <button
            className={
              'mt-4 bg-rose-700 text-white px-4 py-2 rounded text-sm ' +
              (savingSecuritySettings ? 'opacity-50 pointer-events-none' : '')
            }
            onClick={saveSecuritySettings}
            disabled={savingSecuritySettings}
          >
            {savingSecuritySettings ? 'Saving...' : 'Save security settings'}
          </button>
        </div>

        {/* SMART TRIP AUTOMATION */}
        <div id="settings-automation" className="scroll-mt-40 border border-violet-200 rounded-lg p-4 bg-violet-50/60">
          <SettingsSectionHeading
                icon={Route}
                iconClassName="bg-violet-100 text-violet-700"
                className="font-semibold mb-2 text-sm text-violet-900"
              >
                Smart Trip Automation
              </SettingsSectionHeading>
          <p className="text-xs text-gray-700 mb-4">
            Control whether teachers can see the future AutoPilot option in the mobile app.
            Smart Trip Assist remains ask-before Check-in / Check-out in the current version.
          </p>

          <div className="space-y-4">
            <div className="rounded-md border border-violet-100 bg-white/80 p-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-sm font-semibold text-gray-800">
                    Smart Trip Assist
                  </div>
                  <p className="text-[11px] text-gray-600 mt-1">
                    Smart Trip Assist can detect arrival/departure during an active Trip Log and ask before Check-in or Check-out.
                  </p>
                </div>
                <div className="flex items-center gap-2 sm:justify-end">
                  <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800">
                    Enabled
                  </span>
                  <span className="text-[11px] text-gray-500">
                    Read-only
                  </span>
                </div>
              </div>
            </div>

            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-1"
                checked={!!smartTripAutomationSettings.autopilot_available}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setSmartTripAutomationSettings((prev) => ({
                    ...prev,
                    smart_assist_enabled: true,
                    autopilot_available: checked,
                    allow_user_opt_in: checked ? !!prev.allow_user_opt_in : false,
                  }));
                }}
                disabled={smartTripAutomationLoading || savingSmartTripAutomation}
              />
              <span>
                <span className="block text-sm font-medium text-gray-800">
                  Allow AutoPilot option in teacher mobile apps
                </span>
                <span className="block text-[11px] text-gray-600 mt-1">
                  Makes the future AutoPilot option visible in teacher Profile/Settings when supported by the mobile app.
                </span>
              </span>
            </label>

            <label
              className={
                'flex items-start gap-2 ' +
                (!smartTripAutomationSettings.autopilot_available ? 'opacity-60' : '')
              }
            >
              <input
                type="checkbox"
                className="mt-1"
                checked={
                  !!smartTripAutomationSettings.autopilot_available &&
                  !!smartTripAutomationSettings.allow_user_opt_in
                }
                onChange={(e) =>
                  setSmartTripAutomationSettings((prev) => ({
                    ...prev,
                    smart_assist_enabled: true,
                    allow_user_opt_in: !!prev.autopilot_available && e.target.checked,
                  }))
                }
                disabled={
                  smartTripAutomationLoading ||
                  savingSmartTripAutomation ||
                  !smartTripAutomationSettings.autopilot_available
                }
              />
              <span>
                <span className="block text-sm font-medium text-gray-800">
                  Allow teachers to opt in to AutoPilot
                </span>
                <span className="block text-[11px] text-gray-600 mt-1">
                  Teachers will be able to opt in from their mobile Profile/Settings when this organization allows it.
                </span>
              </span>
            </label>

            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
              AutoPilot is a future opt-in mode for automatic Check-in and Check-out while Trip Log is active.
              Enabling this setting only makes the option visible/available in mobile Profile/Settings.
              It does not activate automatic Check-in or Check-out in this version.
            </div>
          </div>

          <button
            className={
              'mt-4 bg-violet-700 text-white px-4 py-2 rounded text-sm ' +
              (savingSmartTripAutomation ? 'opacity-50 pointer-events-none' : '')
            }
            onClick={saveSmartTripAutomationSettings}
            disabled={savingSmartTripAutomation || smartTripAutomationLoading}
          >
            {savingSmartTripAutomation ? 'Saving...' : 'Save Smart Trip Automation settings'}
          </button>
        </div>

        {/* ORG BILLING & INVOICES */}
        <div id="settings-billing" className="scroll-mt-40 border border-indigo-200 rounded-lg p-4 bg-indigo-50/70">
          <div className="flex items-center justify-between mb-2">
            <div>
              <SettingsSectionHeading
                icon={FileText}
                iconClassName="bg-indigo-100 text-indigo-700"
                className="font-semibold text-sm text-indigo-900"
              >
                Billing & invoices
              </SettingsSectionHeading>
              <p className="text-[11px] text-gray-600">
                Download your subscription invoices as PDF for this organization.
              </p>
            </div>
          </div>

          {orgInvoicesError && (
            <div className="mb-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              {orgInvoicesError}
            </div>
          )}

          {orgInvoicesLoading ? (
            <div className="text-xs text-gray-600">Loading invoices…</div>
          ) : orgInvoices.length === 0 ? (
            <div className="text-xs text-gray-500">
              No invoices found for this organization yet.
            </div>
          ) : (
            <div className="mt-2 border border-indigo-100 rounded-md bg-white overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-indigo-50 border-b border-indigo-100">
                  <tr>
                    <th className="text-left px-2 py-1">Invoice</th>
                    <th className="text-left px-2 py-1">Period</th>
                    <th className="text-right px-2 py-1">Amount</th>
                    <th className="text-left px-2 py-1">Status</th>
                    <th className="text-center px-2 py-1">PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {orgInvoices.map((inv) => {
                    const currency = inv.currency || 'USD';
                    const pdfUrl = getInvoicePdfUrl(inv);
                    return (
                      <tr key={inv.id} className="border-t border-indigo-50">
                        <td className="px-2 py-1 align-top">
                          <div className="font-semibold text-gray-800">
                            {inv.invoice_number}
                          </div>
                          <div className="text-[10px] text-gray-400">
                            {inv.invoice_date}
                          </div>
                        </td>
                        <td className="px-2 py-1 align-top text-gray-700">
                          {inv.period_start} – {inv.period_end}
                        </td>
                        <td className="px-2 py-1 align-top text-right text-gray-800">
                          {formatMoney(inv.amount)} {currency}
                        </td>
                        <td className="px-2 py-1 align-top">
                          <span
                            className={
                              'px-2 py-0.5 rounded-full text-[10px] ' +
                              invoiceStatusBadgeClass(inv.status)
                            }
                          >
                            {inv.status}
                          </span>
                        </td>
                        <td className="px-2 py-1 align-top text-center">
                          {pdfUrl ? (
                            <button
                              type="button"
                              onClick={() => window.open(pdfUrl, '_blank', 'noopener,noreferrer')}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-indigo-600 text-white text-[10px] hover:bg-indigo-700"
                              title="Open invoice PDF in a new tab"
                            >
                              <Download size={12} />
                              PDF
                            </button>
                          ) : (
                            <span className="text-[10px] text-gray-400">N/A</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ACCOUNT SECURITY / PASSWORD */}
        <div className="border border-purple-200 rounded-lg p-4 bg-purple-50/70">
          <SettingsSectionHeading
                icon={KeyRound}
                iconClassName="bg-purple-100 text-purple-700"
                className="font-semibold mb-1 text-sm text-purple-900"
              >
                Account security
              </SettingsSectionHeading>
          <p className="text-xs text-gray-700 mb-3">
            {hasPassword === null
              ? 'You can set or change your password for direct email + password login.'
              : isFirstPassword
              ? 'Set a password for your account. After this, you can sign in with email + password in addition to magic links.'
              : 'Change your password. You will use this for classic email + password login.'}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {hasPassword !== false && (
              <div>
                <label className="block text-xs text-gray-700 mb-1">
                  Current password
                </label>
                <input
                  type="password"
                  className="border p-2 rounded w-full text-sm"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Current password"
                />
              </div>
            )}

            <div>
              <label className="block text-xs text-gray-700 mb-1">
                New password
              </label>
              <input
                type="password"
                className="border p-2 rounded w-full text-sm"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-700 mb-1">
                Confirm new password
              </label>
              <input
                type="password"
                className="border p-2 rounded w-full text-sm"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat new password"
              />
            </div>
          </div>

          {isFirstPassword && (
            <p className="mt-2 text-[11px] text-gray-500">
              Since no password exists yet, you can leave the <b>Current password</b> field empty.
            </p>
          )}

          {passwordError && (
            <div className="mt-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              {passwordError}
            </div>
          )}

          <button
            className={
              'mt-3 bg-purple-700 text-white px-4 py-2 rounded text-sm ' +
              (savingPassword ? 'opacity-50 pointer-events-none' : '')
            }
            onClick={savePassword}
            disabled={savingPassword}
          >
            {savingPassword ? 'Saving...' : isFirstPassword ? 'Set password' : 'Change password'}
          </button>
        </div>
      </div>

      {/* LOCATION MODAL (Add/Edit) */}
      {locModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded shadow-lg p-4 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm">
                {locEditingId ? 'Edit location' : 'Add location'}
              </h3>
              <button
                className="text-xs text-gray-500"
                onClick={() => setLocModalOpen(false)}
                disabled={locSubmitting}
              >
                ✕ Close
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
              <div className="md:col-span-6">
                <label className="block text-[11px] text-gray-700 mb-0.5">
                  Location name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  className="border px-2 py-1 rounded w-full text-xs"
                  value={locName}
                  onChange={(e) => setLocName(e.target.value)}
                  placeholder="e.g. Downtown Clinic"
                  disabled={locSubmitting}
                />
              </div>

              <div className="md:col-span-6">
                <label className="block text-[11px] text-gray-700 mb-0.5">Street</label>
                <input
                  type="text"
                  className="border px-2 py-1 rounded w-full text-xs"
                  value={locStreet}
                  onChange={(e) => setLocStreet(e.target.value)}
                  placeholder="123 Main St"
                  disabled={locSubmitting}
                />
              </div>

              <div className="md:col-span-3">
                <label className="block text-[11px] text-gray-700 mb-0.5">City</label>
                <input
                  type="text"
                  className="border px-2 py-1 rounded w-full text-xs"
                  value={locCity}
                  onChange={(e) => setLocCity(e.target.value)}
                  placeholder="San Diego"
                  disabled={locSubmitting}
                />
              </div>

              <div className="md:col-span-1">
                <label className="block text-[11px] text-gray-700 mb-0.5">State</label>
                <input
                  type="text"
                  className="border px-2 py-1 rounded w-full text-xs"
                  value={locState}
                  onChange={(e) => setLocState(e.target.value)}
                  placeholder="CA"
                  disabled={locSubmitting}
                />
              </div>

              <div className="md:col-span-1">
                <label className="block text-[11px] text-gray-700 mb-0.5">ZIP</label>
                <input
                  type="text"
                  className="border px-2 py-1 rounded w-full text-xs"
                  value={locZip}
                  onChange={(e) => setLocZip(e.target.value)}
                  placeholder="92101"
                  disabled={locSubmitting}
                />
              </div>

              <div className="md:col-span-1">
                <label className="block text-[11px] text-gray-700 mb-0.5">Country</label>
                <input
                  type="text"
                  className="border px-2 py-1 rounded w-full text-xs"
                  value={locCountry}
                  onChange={(e) => setLocCountry(e.target.value)}
                  placeholder="USA"
                  disabled={locSubmitting}
                />
              </div>

              <div className="md:col-span-6">
                <label className="flex items-center gap-2 text-xs text-gray-700">
                  <input type="checkbox" checked={true} disabled={true} />
                  Geocoding is always enabled to store lat/lon for routing and distance calculations.
                </label>
              </div>
            </div>

            <div className="mt-3 flex justify-end gap-2">
              <button
                className="px-3 py-1 text-xs border rounded"
                onClick={() => setLocModalOpen(false)}
                disabled={locSubmitting}
              >
                Cancel
              </button>
              <button
                className={
                  'px-3 py-1 text-xs rounded text-white bg-slate-800 ' +
                  (locSubmitting ? 'opacity-50 pointer-events-none' : '')
                }
                onClick={submitLocation}
                disabled={locSubmitting}
              >
                {locSubmitting ? 'Saving...' : locEditingId ? 'Save' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MAILING LIST MODAL */}
      {mailingModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded shadow-lg p-4 w-full max-w-3xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm">
                Parent email list (visit notifications)
              </h3>
              <button
                className="text-xs text-gray-500"
                onClick={() => setMailingModalOpen(false)}
                disabled={mailingSaving}
              >
                ✕ Close
              </button>
            </div>

            <p className="text-xs text-gray-600 mb-2">
              Only parents checked below will receive visit reminder emails.
              Others will be excluded from these notifications.
            </p>

            {mailingLoading ? (
              <div className="text-sm text-gray-600">Loading...</div>
            ) : (
              <table className="w-full text-xs border">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-1 text-left">Receive</th>
                    <th className="border p-1 text-left">Parent</th>
                    <th className="border p-1 text-left">Email</th>
                    <th className="border p-1 text-left">Students</th>
                  </tr>
                </thead>
                <tbody>
                  {mailingParents.map((p) => (
                    <tr key={p.parent_id}>
                      <td className="border p-1 text-center">
                        <input
                          type="checkbox"
                          checked={!!p.receive_visit_notifications}
                          onChange={() => toggleParentMailing(p.parent_id)}
                          disabled={mailingSaving}
                        />
                      </td>
                      <td className="border p-1">
                        {p.last_name ? `${p.last_name}, ` : ''}
                        {p.first_name}
                      </td>
                      <td className="border p-1">{p.email}</td>
                      <td className="border p-1">
                        {renderStudentsForMailing(p) || (
                          <span className="text-[10px] text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="mt-3 flex justify-end gap-2">
              <button
                className="px-3 py-1 text-xs border rounded"
                onClick={() => setMailingModalOpen(false)}
                disabled={mailingSaving}
              >
                Cancel
              </button>
              <button
                className={
                  'px-3 py-1 text-xs rounded text-white bg-green-700 ' +
                  (mailingSaving ? 'opacity-50 pointer-events-none' : '')
                }
                onClick={saveMailingModal}
                disabled={mailingSaving}
              >
                {mailingSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SettingsTimezone;















